import axios from 'axios';
import { attributionForSubmit } from '../utils/careerPilotAttribution';
import { loadRazorpay } from './paymentApi';
import { visitorId, sessionId } from '../pages/Passport/activityBeacon';
import { celebrateMembership } from '../components/Celebration/celebrate';

const BASE = (process.env.REACT_APP_API_URL || '/api/v1') + '/passport';
/**
 * The canonical mount. `/passport` is kept as a legacy alias server-side, and the two
 * serve the same router — new calls use this one rather than replacing a substring at
 * every call site, which is what the older methods below still do.
 */
const CP = (process.env.REACT_APP_API_URL || '/api/v1') + '/careerpilot';
const auth = () => {
  const token = localStorage.getItem('token');
  const tenantId = localStorage.getItem('tenantId');
  // The visitor headers ride on every call so the server-side half of the activity trail can be
  // joined to the browser-side half. Without them an API row has no trail to belong to, and the
  // timeline would show what the server did with no idea which person it was doing it for —
  // least of all before sign-in, where there is no user id to fall back on.
  let trail: Record<string, string> = {};
  try { trail = { 'X-CP-Visitor': visitorId(), 'X-CP-Session': sessionId() }; } catch { trail = {}; }
  return { ...trail, ...(token && { Authorization: `Bearer ${token}` }), ...(tenantId && { 'X-Tenant-Id': tenantId }) };
};

export type OnboardingFieldType = 'text' | 'textarea' | 'select' | 'number' | 'date' | 'phone' | 'email';
export interface OnboardingField {
  key: string; label: string; type: OnboardingFieldType | string; required: boolean; locked?: boolean; options?: string[]; order: number;
  /** Hint inside the empty input. */
  placeholder?: string;
  /** Whether the field is on the sign-up form. Absent means on. */
  enabled?: boolean;
  /** Added by an admin; only these can be deleted. */
  custom?: boolean;
}
/** The registration window as the server resolves it right now. */
export interface RegistrationWindow {
  open: boolean; reason: 'NOT_YET_OPEN' | 'CLOSED' | null;
  opensAt: string | null; closesAt: string | null; academicSession?: string;
}
export interface Entitlement { featureKey: string; label: string; tier: 'free' | 'paid'; }
export interface PassportConfig {
  _id?: string; enabled: boolean; assessmentMode: 'deterministic' | 'ai';
  onboardingFields: OnboardingField[]; entitlements: Entitlement[];
  priceInr: number;
  /**
   * Whether membership checkout takes real money. Absent on older configs, which means live.
   * CareerPilot membership only — hackathon registration is a different purpose and unaffected.
   */
  paymentMode?: 'live' | 'test';
  /** How long access lasts after paying. */
  membershipMonths: number;
  /** How many days of work a plan covers. Capped at 90 by the planner. */
  roadmapDays?: number;
  /** Days of their own roadmap a learner sees before membership (1–30, default 7). */
  roadmapPreviewDays?: number;
  /** The length of a Foundation journey composed from now on. Ninety when unset. */
  foundationProgramDays?: number;
  /**
   * Programme length and price PER YEAR, keyed by stage.
   *
   * `priceInr` is the tenant's single price and `foundationProgramDays` its single length;
   * both remain the fallback for any stage with no entry here. Without these a tenant selling
   * four years sells them all at one price and plans them all at one length, which is how a
   * final-year came to be offered a first-year's price.
   *
   * Mongoose stores them as Maps and returns plain objects on a lean read, which is what the
   * API sends. A stage absent from the object is not zero — it is "use the single value".
   */
  programDaysByStage?: Record<string, number>;
  priceInrByStage?: Record<string, number>;
  /**
   * Whether daily missions follow authored Concept Learning Units.
   *
   * Off is not a degraded mode: with it off the mission engine behaves exactly as it did
   * before the layer existed, resolving the first mapped resource for a skill.
   */
  conceptLearningEnabled?: boolean;
  /** Curriculum engine switches. Allow-lists: they can only move a stage or student onto UNIT. */
  megaCurriculumEnabled?: boolean;
  megaCurriculumStages?: string[];
  megaCurriculumStudentIds?: string[];
  /** Sign-ups are refused outside these dates (either may be empty = open-ended). ISO strings. */
  registrationOpensAt?: string | null;
  registrationClosesAt?: string | null;
  /** The intake label, e.g. "2026-2027". */
  academicSession?: string;
}

/** Which engine plans students' learning, as the server resolves it. */
export interface CurriculumEngineSummary {
  foundationMode: 'TOPIC' | 'UNIT';
  megaCurriculumEnabled: boolean;
  megaCurriculumStages: string[];
  megaCurriculumStudentIds: string[];
  unitCapableStages: string[];
  stages: { stage: string; label: string; mode: 'TOPIC' | 'UNIT' }[];
}

/**
 * Whether each year the unit engine CAN serve actually has a curriculum on this tenant.
 *
 * The engine summary says which switch a year is on; it cannot say whether anything is behind
 * it. Both are needed before a toggle is safe: switching on a year with no units moves its
 * students from a wrong roadmap to no roadmap at all.
 */
export interface StageReadiness {
  configured: boolean;
  reason: string | null;
  publishedUnits: number | null;
  message: string | null;
}

/** One skill's share of the assessable pool. */
export interface PoolCoverageRow {
  skillKey: string;
  skillName: string;
  /** Mapped, approved questions — what a paper can actually draw from. */
  approved: number;
  /** Drafted but not yet reviewed. Reachable by nobody until approved. */
  pending: number;
}

export interface DraftOption { text: string; isCorrect: boolean }

export interface QuestionDraft {
  _id: string;
  skillKey: string;
  difficulty: 'easy' | 'medium' | 'hard';
  question: string;
  options: DraftOption[];
  explanation: string;
  codeSnippet?: string;
  language?: string;
  /** Why a student would pick each wrong option. Empty is itself a warning. */
  distractorRationale?: string[];
  status: 'pending' | 'approved' | 'rejected';
  /** Who the question is for. Empty on every axis means everyone. */
  audienceRoles?: string[];
  audienceYears?: string[];
  audienceCourses?: string[];
  audienceBranches?: string[];
  /** Written by a person rather than the model. */
  manual?: boolean;
  /** What the automatic checks noticed. Not errors — places to look first. */
  warnings: string[];
  batchId: string;
  generatedBy: string;
  createdAt: string;
  reviewNote?: string;
  approvedQuestionId?: string;
}

export interface DraftBatchReport {
  batchId: string;
  skillKey: string;
  requested: number;
  returned: number;
  stored: number;
  flagged: number;
  dropped: { reason: string; question: string }[];
}

export const passportApi = {
  getConfig: async (): Promise<{ config: PassportConfig; platformEnabled: boolean; engine?: CurriculumEngineSummary; foundation?: FoundationReadiness; stageReadiness?: Record<string, StageReadiness> }> => {
    const { data } = await axios.get(`${BASE}/config`, { headers: auth() });
    return data;
  },
  /**
   * Returns the whole envelope, not just the config.
   *
   * Saving the engine switches changes which engine every year resolves to, and the server is
   * the only thing that knows the answer — capability is applied after the switches. Returning
   * `config` alone meant the screen had to guess, or reload, to show what it had just done.
   */
  updateConfig: async (patch: Partial<PassportConfig>): Promise<{
    config: PassportConfig; engine?: CurriculumEngineSummary;
    foundation?: FoundationReadiness; stageReadiness?: Record<string, StageReadiness>;
    /** Years whose days changed, with the new length: their roadmaps are being resized. */
    roadmapsResizing?: Record<string, number> | null;
  }> => {
    const { data } = await axios.put(`${BASE}/config`, patch, { headers: auth() });
    return data;
  },
  listStudents: async (search = ''): Promise<any[]> => {
    const { data } = await axios.get(`${BASE}/students`, { headers: auth(), params: { search } });
    return data.students;
  },
  /** Admin: what this member has written on missions that have no other surface. */
  listStudentAnswers: async (studentId: string): Promise<{
    name: string; email?: string;
    answers: {
      day: number; key: string; title: string; detail: string; category: string | null;
      answer: string; at: string;
      feedback?: string | null;
      extract?: { targetRole?: string | null; skills?: string[]; gaps?: string[]; specificity?: number; flag?: string } | null;
    }[];
  }> => {
    const { data } = await axios.get(`${BASE}/students/${studentId}/answers`, { headers: auth() });
    return data;
  },
  /** Admin: every mock interview this member has sat, newest first. */
  listStudentInterviews: async (studentId: string): Promise<{
    name: string; email?: string;
    interviews: {
      id: string; role: string; status: string; askedCount: number; answers: number;
      startedAt: string; completedAt: string | null;
      evaluation: InterviewSession['evaluation'];
      transcript: InterviewTurn[];
    }[];
  }> => {
    const { data } = await axios.get(`${BASE}/students/${studentId}/interviews`, { headers: auth() });
    return data;
  },
  convert: async (studentId: string): Promise<any> => {
    const { data } = await axios.post(`${BASE}/convert`, { studentId }, { headers: auth() });
    return data;
  },

  createMember: async (body: { firstName: string; lastName?: string; email: string; phone?: string }): Promise<any> => {
    const { data } = await axios.post(`${BASE}/members`, body, { headers: auth() });
    return data;
  },
  updateMember: async (userId: string, body: Record<string, any>): Promise<any> => {
    const { data } = await axios.put(`${BASE}/members/${userId}`, body, { headers: auth() });
    return data;
  },
  setMemberActive: async (userId: string, active: boolean): Promise<any> => {
    const { data } = await axios.post(`${BASE}/members/${userId}/active`, { active }, { headers: auth() });
    return data;
  },
  grantMembership: async (userId: string, days: number, reason: string): Promise<any> => {
    const { data } = await axios.post(`${BASE}/members/${userId}/grant`, { days, reason }, { headers: auth() });
    return data;
  },
  revokeMembership: async (userId: string): Promise<any> => {
    const { data } = await axios.delete(`${BASE}/members/${userId}/grant`, { headers: auth() });
    return data;
  },
  /** Trial plans: what a trial opens and for how long (CareerPilot → Trial plans). */
  listTrialPlans: async (): Promise<{ plans: TrialPlan[]; catalogue: TrialCatalogueItem[]; maxDays: number; maxPicked: number }> => {
    const { data } = await axios.get(`${BASE}/trial-plans`, { headers: auth() });
    return data;
  },
  /** Practice-library problems an admin can name in a plan. */
  searchTrialProblems: async (q: string): Promise<TrialProblem[]> => {
    const { data } = await axios.get(`${BASE}/trial-plans/problems`, { params: { q }, headers: auth() });
    return data.problems || [];
  },
  saveTrialPlan: async (id: string | null, body: Omit<TrialPlan, 'id' | 'running' | 'updatedAt' | 'practiceProblems'> & { practiceProblemIds: string[] }): Promise<{ plan: TrialPlan }> => {
    const { data } = id
      ? await axios.put(`${BASE}/trial-plans/${id}`, body, { headers: auth() })
      : await axios.post(`${BASE}/trial-plans`, body, { headers: auth() });
    return data;
  },
  deleteTrialPlan: async (id: string): Promise<void> => {
    await axios.delete(`${BASE}/trial-plans/${id}`, { headers: auth() });
  },
  startTrial: async (userId: string, planId: string, reason: string): Promise<{ trial: TrialStatus | null }> => {
    const { data } = await axios.post(`${BASE}/members/${userId}/trial`, { planId, reason }, { headers: auth() });
    return data;
  },
  endTrial: async (userId: string): Promise<void> => {
    await axios.delete(`${BASE}/members/${userId}/trial`, { headers: auth() });
  },
  memberTrial: async (userId: string): Promise<{ trial: TrialStatus | null; last: any }> => {
    const { data } = await axios.get(`${BASE}/members/${userId}/trial`, { headers: auth() });
    return data;
  },
  deleteMember: async (userId: string): Promise<any> => {
    const { data } = await axios.delete(`${BASE}/members/${userId}`, { headers: auth() });
    return data;
  },
  me: async (): Promise<any> => {
    const { data } = await axios.get(`${BASE}/me`, { headers: auth() });
    return data;
  },
  setPassword: async (password: string): Promise<{ success: boolean }> => {
    const { data } = await axios.post(`${BASE}/set-password`, { password }, { headers: auth() });
    return data;
  },

  // Assessment — student
  getAssessment: async (): Promise<{ title: string; questions: AssessQuestion[] }> => {
    const { data } = await axios.get(`${BASE}/assessment`, { headers: auth() });
    return data;
  },
  submitAssessment: async (answers: { questionId: string; chosen: number }[]): Promise<{ result: AssessResult }> => {
    const { data } = await axios.post(`${BASE}/assessment/submit`, { answers }, { headers: auth() });
    return data;
  },
  getResult: async (): Promise<{ result: AssessResult | null }> => {
    const { data } = await axios.get(`${BASE}/assessment/result`, { headers: auth() });
    return data;
  },

  // Assessment — admin
  /**
   * The bank, the categories in force, and what each category is carrying.
   * Usage comes back with the list because the delete button needs it BEFORE it is
   * pressed — the server refuses to remove a category that still holds content.
   */
  getAssessmentAdmin: async (): Promise<{ assessment: AssessmentBank; categories: AssessCategory[]; usage: CategoryUsage[] }> => {
    const { data } = await axios.get(`${BASE}/assessment/admin`, { headers: auth() });
    return data;
  },
  // ── Activity trail ──────────────────────────────────────────────────────────
  activitySessions: async (q: { from?: string; to?: string; search?: string; deviceType?: string;
                               onlyFailures?: boolean; limit?: number; skip?: number }):
    Promise<{ sessions: ActivitySession[]; total: number; limit: number; skip: number }> => {
    const r = await axios.get(`${BASE.replace('/passport', '/careerpilot')}/admin/activity/sessions`,
      { headers: auth(), params: q });
    return r.data;
  },
  activitySummary: async (q: { from?: string; to?: string; deviceType?: string }): Promise<ActivitySummary> => {
    const r = await axios.get(`${BASE.replace('/passport', '/careerpilot')}/admin/activity/summary`,
      { headers: auth(), params: q });
    return r.data;
  },
  activityTimeline: async (visitorId: string): Promise<{ visitorId: string; events: ActivityEvent[] }> => {
    const r = await axios.get(
      `${BASE.replace('/passport', '/careerpilot')}/admin/activity/timeline/${encodeURIComponent(visitorId)}`,
      { headers: auth() });
    return r.data;
  },
  // ── The student's ninety days ─────────────────────────────────────
  myFoundationJourney: async (): Promise<FoundationJourney> => {
    const r = await axios.get(`${CP}/me/foundation-journey`, { headers: auth() });
    return r.data;
  },
  myFoundationJourneyDay: async (day: number): Promise<FoundationJourneyDay> => {
    const r = await axios.get(`${CP}/me/foundation-journey/day/${day}`, { headers: auth() });
    return r.data;
  },
  /** Admin: one member's ninety-day Foundation journey, with the unit behind each day. Read-only. */
  getStudentFoundationJourney: async (studentId: string): Promise<AdminFoundationJourney> => {
    const r = await axios.get(`${CP}/students/${encodeURIComponent(studentId)}/foundation-journey`, { headers: auth() });
    return r.data;
  },
  // ── Mega curriculum: Learning Units ───────────────────────────────
  megaCurriculum: async (stage = 'foundation'): Promise<{
    stageKey: string; curriculumTitle: string | null;
    rows: MegaCurriculumTopicRow[]; orphaned: CurriculumLearningUnit[];
    summary: MegaCurriculumSummary;
  }> => {
    const r = await axios.get(`${CP}/curriculum-units`, { headers: auth(), params: { stage } });
    return r.data;
  },
  /** What a student of each direction actually receives — the personalisation gap, measured. */
  directionCoverage: async (stage = 'foundation'): Promise<DirectionCoverageReport> => {
    const r = await axios.get(`${CP}/curriculum-units/direction-coverage`, { params: { stage }, headers: auth() });
    return r.data;
  },
  megaCurriculumOptions: async (stage = 'foundation'): Promise<MegaCurriculumOptions> => {
    const r = await axios.get(`${CP}/curriculum-units/options`, { headers: auth(), params: { stage } });
    return r.data;
  },
  getCurriculumUnit: async (unitCode: string): Promise<{ unit: CurriculumLearningUnit; bundle: UnitBundle }> => {
    const r = await axios.get(`${CP}/curriculum-units/${encodeURIComponent(unitCode)}`, { headers: auth() });
    return r.data;
  },
  saveCurriculumUnit: async (unitCode: string, body: Partial<CurriculumLearningUnit>): Promise<{
    unit: CurriculumLearningUnit; created: boolean; warning: string | null;
  }> => {
    const r = await axios.put(`${CP}/curriculum-units/${encodeURIComponent(unitCode)}`, body, { headers: auth() });
    return r.data;
  },
  publishCurriculumUnit: async (unitCode: string): Promise<{ published: boolean; unit?: CurriculumLearningUnit; message?: string }> => {
    const r = await axios.post(`${CP}/curriculum-units/${encodeURIComponent(unitCode)}/publish`, {}, { headers: auth() });
    return r.data;
  },
  /**
   * A PUBLISHED unit on students' journeys is refused with 409 `UNIT_IN_LIVE_JOURNEYS` and the number
   * of students it touches; send `confirmLiveJourneys` only after the admin has seen that number.
   */
  setCurriculumUnitStatus: async (unitCode: string, status: 'DRAFT' | 'ARCHIVED', confirmLiveJourneys = false): Promise<{ unit: CurriculumLearningUnit }> => {
    const r = await axios.post(`${CP}/curriculum-units/${encodeURIComponent(unitCode)}/status`, { status, confirmLiveJourneys }, { headers: auth() });
    return r.data;
  },
  reorderCurriculumUnits: async (order: { unitCode: string; displayOrder: number }[]): Promise<{ reordered: number }> => {
    const r = await axios.post(`${CP}/curriculum-units/reorder`, { order }, { headers: auth() });
    return r.data;
  },
  unitContent: async (unitCode: string): Promise<UnitContent> => {
    const r = await axios.get(`${CP}/curriculum-units/${encodeURIComponent(unitCode)}/content`, { headers: auth() });
    return r.data;
  },
  /** Read-only: the day this unit gives a student, built as a journey day is built. */
  unitStudentPreview: async (unitCode: string): Promise<UnitStudentPreview> => {
    const r = await axios.get(`${CP}/curriculum-units/${encodeURIComponent(unitCode)}/student-preview`, { headers: auth() });
    return r.data;
  },
  /** The day's video shooting script (admin-only). null when none has been written. */
  getVideoScript: async (unitCode: string): Promise<VideoScript | null> => {
    const r = await axios.get(`${CP}/curriculum-units/${encodeURIComponent(unitCode)}/script`, { headers: auth() });
    return r.data?.data || null;
  },
  saveVideoScript: async (unitCode: string, body: VideoScriptInput): Promise<VideoScript> => {
    const r = await axios.put(`${CP}/curriculum-units/${encodeURIComponent(unitCode)}/script`, body, { headers: auth() });
    return r.data.data;
  },
  deleteVideoScript: async (unitCode: string): Promise<void> => {
    await axios.delete(`${CP}/curriculum-units/${encodeURIComponent(unitCode)}/script`, { headers: auth() });
  },
  attachUnitContent: async (unitCode: string, contentId: string): Promise<{ attached: boolean }> => {
    const r = await axios.post(
      `${CP}/curriculum-units/${encodeURIComponent(unitCode)}/content/${encodeURIComponent(contentId)}`,
      {}, { headers: auth() });
    return r.data;
  },
  /**
   * The whole content list, in the order a student should meet it.
   *
   * Sent whole rather than as "move this one up": the server numbers every row, because leaving
   * the unmoved ones unnumbered would send them all below the ones that have a position.
   */
  reorderUnitContent: async (unitCode: string, contentIds: string[]): Promise<{ reordered: number }> => {
    const r = await axios.post(
      `${CP}/curriculum-units/${encodeURIComponent(unitCode)}/content/reorder`,
      { contentIds }, { headers: auth() });
    return r.data;
  },
  detachUnitContent: async (unitCode: string, contentId: string): Promise<{ detached: boolean }> => {
    const r = await axios.delete(
      `${CP}/curriculum-units/${encodeURIComponent(unitCode)}/content/${encodeURIComponent(contentId)}`,
      { headers: auth() });
    return r.data;
  },
  deleteCurriculumUnit: async (unitCode: string): Promise<{ deleted: boolean }> => {
    const r = await axios.delete(`${CP}/curriculum-units/${encodeURIComponent(unitCode)}`, { headers: auth() });
    return r.data;
  },
  // ── What a unit measures with ─────────────────────────────────────
  unitAssessments: async (unitCode: string): Promise<UnitAssessments> => {
    const r = await axios.get(
      `${CP}/curriculum-units/${encodeURIComponent(unitCode)}/assessments`, { headers: auth() });
    return r.data;
  },
  bindUnitAssessment: async (
    unitCode: string, kind: 'QUIZ' | 'ASSIGNMENT', id: string,
  ): Promise<{ bound: boolean }> => {
    const seg = kind === 'QUIZ' ? 'quiz' : 'assignment';
    const r = await axios.post(
      `${CP}/curriculum-units/${encodeURIComponent(unitCode)}/${seg}/${encodeURIComponent(id)}`,
      {}, { headers: auth() });
    return r.data;
  },
  unbindUnitAssessment: async (
    unitCode: string, kind: 'QUIZ' | 'ASSIGNMENT', id: string,
  ): Promise<{ unbound: boolean }> => {
    const seg = kind === 'QUIZ' ? 'quiz' : 'assignment';
    const r = await axios.delete(
      `${CP}/curriculum-units/${encodeURIComponent(unitCode)}/${seg}/${encodeURIComponent(id)}`,
      { headers: auth() });
    return r.data;
  },
  createUnitAssessment: async (
    unitCode: string, kind: 'QUIZ' | 'ASSIGNMENT', title?: string, type?: string,
  ): Promise<{ created: boolean; id: string }> => {
    const r = await axios.post(
      `${CP}/curriculum-units/${encodeURIComponent(unitCode)}/assessments`,
      { kind, title, ...(type ? { type } : {}) }, { headers: auth() });
    return r.data;
  },
  // ── Module and topic order ────────────────────────────────────────
  reorderStageModules: async (stage: string, order: string[]): Promise<any> => {
    const r = await axios.post(
      `${CP}/stage-curriculum/${encodeURIComponent(stage)}/modules/reorder`,
      { order }, { headers: auth() });
    return r.data;
  },
  reorderStageTopics: async (stage: string, moduleCode: string, order: string[]): Promise<any> => {
    const r = await axios.post(
      `${CP}/stage-curriculum/${encodeURIComponent(stage)}/topics/reorder`,
      { moduleCode, order }, { headers: auth() });
    return r.data;
  },
  // ── Learning Studio ─────────────────────────────────────────────────────────
  studioConcepts: async (): Promise<{ concepts: StudioConcept[]; summary: any }> => {
    const r = await axios.get(`${BASE.replace('/passport', '/careerpilot')}/concept-learning-units/concepts`, { headers: auth() });
    return r.data;
  },
  learningUnitBySkill: async (skillKey: string): Promise<{
    skill: any; unit: LearningUnit | null; versions: any[];
    readiness: UnitReadiness | null; resources: StudioResource[];
  }> => {
    const r = await axios.get(`${BASE.replace('/passport', '/careerpilot')}/concept-learning-units/by-skill/${encodeURIComponent(skillKey)}`, { headers: auth() });
    return r.data;
  },
  saveLearningUnit: async (skillKey: string, unit: Partial<LearningUnit>):
    Promise<{ unit: LearningUnit; readiness: UnitReadiness }> => {
    const r = await axios.put(`${BASE.replace('/passport', '/careerpilot')}/concept-learning-units/by-skill/${encodeURIComponent(skillKey)}`, unit, { headers: auth() });
    return r.data;
  },
  publishLearningUnit: async (id: string): Promise<{ published: boolean; version?: number; readiness: UnitReadiness; message?: string }> => {
    const r = await axios.post(`${BASE.replace('/passport', '/careerpilot')}/concept-learning-units/${id}/publish`, {}, { headers: auth() });
    return r.data;
  },
  previewLearningUnit: async (id: string): Promise<{ unit: any; steps: any[] }> => {
    const r = await axios.get(`${BASE.replace('/passport', '/careerpilot')}/concept-learning-units/${id}/preview`, { headers: auth() });
    return r.data;
  },
  saveAssessment: async (patch: { title?: string; maxQuestions?: number; questions?: AssessQuestionFull[] }): Promise<{ assessment: AssessmentBank }> => {
    const { data } = await axios.put(`${BASE}/assessment/admin`, patch, { headers: auth() });
    return data;
  },
  resetAssessment: async (): Promise<{ assessment: AssessmentBank }> => {
    const { data } = await axios.post(`${BASE}/assessment/reset`, {}, { headers: auth() });
    return data;
  },
  /**
   * Replace the whole category list. Rejects with 409 and an `inUse` breakdown when a
   * removed category still has questions, missions or pathways pointing at it.
   */
  saveCategories: async (categories: AssessCategory[]): Promise<{ categories: AssessCategory[]; removed: string[] }> => {
    const { data } = await axios.put(`${BASE}/assessment/categories`, { categories }, { headers: auth() });
    return data;
  },

  // ── Pathway curriculum (admin-authored days) ──
  listCurricula: async (): Promise<{ tracks: CurriculumTrack[]; overrides: { pathwayKey: string; days: number }[] }> => {
    const { data } = await axios.get(`${BASE}/curriculum`, { headers: auth() });
    return data;
  },
  getCurriculum: async (key: string): Promise<CurriculumDoc> => {
    const { data } = await axios.get(`${BASE}/curriculum/${encodeURIComponent(key)}`, { headers: auth() });
    return data;
  },
  saveCurriculum: async (key: string, days: CurriculumDay[]): Promise<{ days: CurriculumDay[] }> => {
    const { data } = await axios.put(`${BASE}/curriculum/${encodeURIComponent(key)}`, { days }, { headers: auth() });
    return data;
  },
  moveCurriculumDay: async (key: string, from: number, to: number): Promise<{ days: CurriculumDay[] }> => {
    const { data } = await axios.post(`${BASE}/curriculum/${encodeURIComponent(key)}/move`, { from, to }, { headers: auth() });
    return data;
  },
  copyCurriculum: async (key: string, from: string): Promise<{ days: CurriculumDay[]; copiedFrom: string }> => {
    const { data } = await axios.post(`${BASE}/curriculum/${encodeURIComponent(key)}/copy`, { from }, { headers: auth() });
    return data;
  },
  /** Appends AI-drafted days after whatever is already written. Nothing is published. */
  draftCurriculum: async (key: string, count: number, brief?: string): Promise<{ days: CurriculumDay[]; added: number }> => {
    const { data } = await axios.post(`${BASE}/curriculum/${encodeURIComponent(key)}/draft`, { count, brief }, { headers: auth() });
    return data;
  },

  // ── Role readiness (Module 8) ──
  /** Derived on request; the target role comes from stored context, never from the client. */
  getMyReadiness: async (): Promise<RoleReadinessResponse> => {
    const { data } = await axios.get(`${BASE}/me/readiness`, { headers: auth() });
    return data;
  },
  /** Admin: one member's readiness, with weights and the workings. */
  getStudentReadiness: async (studentId: string, roleKey?: string): Promise<any> => {
    const { data } = await axios.get(`${BASE}/students/${studentId}/readiness`, {
      headers: auth(), params: roleKey ? { roleKey } : {},
    });
    return data;
  },

  // ── 90-day roadmap (Module 9) ──
  /**
   * The caller's own plan. Distinct from getRoadmap(), which is the mission journey — that
   * answers "what do I do today", this answers "what am I working toward".
   */
  getMySkillRoadmap: async (): Promise<SkillRoadmapResponse> => {
    const { data } = await axios.get(`${BASE}/me/roadmap`, { headers: auth() });
    return data;
  },
  /** Safe to call twice: an existing plan is returned rather than replaced. */
  generateMySkillRoadmap: async (): Promise<SkillRoadmapResponse> => {
    const { data } = await axios.post(`${BASE}/me/roadmap/generate`, {}, { headers: auth() });
    return data;
  },
  /** Explicit rebuild. The previous plan is kept as history, never deleted. */
  replanMySkillRoadmap: async (): Promise<SkillRoadmapResponse> => {
    const { data } = await axios.post(`${BASE}/me/roadmap/replan`, {}, { headers: auth() });
    return data;
  },
  // ── Stage skill sets: what a student with no role is measured against ─────
  listStageSkillSets: async (): Promise<{
    stages: { stage: string; label: string; enabled: boolean; count: number; activeCount: number; version: number }[];
    stageCatalogue: { key: string; label: string; blurb: string }[];
  }> => {
    const { data } = await axios.get(`${BASE}/stage-skill-sets`, { headers: auth() });
    return data;
  },
  /* ── One stage, whole: modules, topics, skills and what can measure them ── */

  listStageCurriculumStages: async (): Promise<{ stages: { key: string; label: string; blurb: string; who: string }[] }> => {
    const { data } = await axios.get(`${BASE}/stage-curriculum`, { headers: auth() });
    return data;
  },
  getStageCurriculum: async (stage: string): Promise<StageCurriculumView> => {
    const { data } = await axios.get(`${BASE}/stage-curriculum/${stage}`, { headers: auth() });
    return data;
  },
  saveStageModule: async (stage: string, body: {
    moduleCode: string; moduleName?: string; displayOrder?: number; blurb?: string;
  }): Promise<StageCurriculumView> => {
    const { data } = await axios.put(`${BASE}/stage-curriculum/${stage}/modules`, body, { headers: auth() });
    return data;
  },
  deleteStageModule: async (stage: string, moduleCode: string): Promise<StageCurriculumView> => {
    const { data } = await axios.delete(
      `${BASE}/stage-curriculum/${stage}/modules/${encodeURIComponent(moduleCode)}`, { headers: auth() });
    return data;
  },
  createStageTopic: async (stage: string, body: StageTopicInput): Promise<StageCurriculumView> => {
    const { data } = await axios.post(`${BASE}/stage-curriculum/${stage}/topics`, body, { headers: auth() });
    return data;
  },
  updateStageTopic: async (stage: string, topicId: string, body: StageTopicInput): Promise<StageCurriculumView> => {
    const { data } = await axios.put(`${BASE}/stage-curriculum/${stage}/topics/${topicId}`, body, { headers: auth() });
    return data;
  },
  deleteStageTopic: async (stage: string, topicId: string): Promise<StageCurriculumView> => {
    const { data } = await axios.delete(`${BASE}/stage-curriculum/${stage}/topics/${topicId}`, { headers: auth() });
    return data;
  },

  getStageSkillSet: async (stage: string): Promise<any> => {
    const { data } = await axios.get(`${BASE}/stage-skill-sets/${stage}`, { headers: auth() });
    return data;
  },
  saveStageSkillSet: async (stage: string, body: {
    label?: string; enabled?: boolean; requirements: any[];
  }): Promise<{ success: boolean; version: number; enabled: boolean }> => {
    const { data } = await axios.put(`${BASE}/stage-skill-sets/${stage}`, body, { headers: auth() });
    return data;
  },

  /** Admin: one member's plan, with the workings and their roadmap history. */
  getStudentSkillRoadmap: async (studentId: string): Promise<any> => {
    const { data } = await axios.get(`${BASE}/students/${studentId}/roadmap`, { headers: auth() });
    return data;
  },

  // ── Personalised assessment + daily plan (Module 10) ──
  /** Starts, or resumes an open paper. Never generates a second one. */
  /**
   * `skillKey` narrows the paper to ONE skill — what a daily plan item's "Check" means.
   * Omitted, the paper covers the whole role, which is the initial assessment.
   */
  startSkillAssessment: async (skillKey?: string): Promise<{
    assessment: SkillAssessment; resumed: boolean; mismatched?: boolean;
  }> => {
    const { data } = await axios.post(
      `${BASE}/me/assessment/personalized/start`,
      skillKey ? { skillKey } : {},
      { headers: auth() },
    );
    return data;
  },
  getSkillAssessment: async (): Promise<{ assessment: SkillAssessment | null }> => {
    const { data } = await axios.get(`${BASE}/me/assessment/personalized`, { headers: auth() });
    return data;
  },
  /** Saves progress without submitting — the paper stays open and nothing is graded. */
  saveSkillAnswers: async (answers: { sourceType: string; sourceId: string; response: any; dontKnow?: boolean }[], position?: number): Promise<{ saved: boolean; answered: number }> => {
    const { data } = await axios.put(`${BASE}/me/assessment/personalized/answers`, { answers, position }, { headers: auth() });
    return data;
  },
  /** Today's roadmap-derived work. */
  getTodaysPlan: async (): Promise<DailyPlanResponse> => {
    const { data } = await axios.get(`${BASE}/me/plan/today`, { headers: auth() });
    return data;
  },
  completeDailyMission: async (key: string): Promise<any> => {
    const { data } = await axios.post(`${BASE}/me/plan/complete`, { key }, { headers: auth() });
    return data;
  },

  // ── Skill check-in (Module 13) ──
  getReassessmentStatus: async (): Promise<ReassessmentStatus> => {
    const { data } = await axios.get(`${BASE}/me/reassessment/status`, { headers: auth() });
    return data;
  },
  startReassessment: async (): Promise<{ attemptId: string; resumed: boolean; targetSkills: { skillKey: string; skillName: string }[] }> => {
    const { data } = await axios.post(`${BASE}/me/reassessment/start`, {}, { headers: auth() });
    return data;
  },
  getReassessmentResult: async (attemptId: string): Promise<ReassessmentResult> => {
    const { data } = await axios.get(`${BASE}/me/reassessment/${attemptId}/result`, { headers: auth() });
    return data;
  },
  getReplanStatus: async (): Promise<ReplanStatusView> => {
    const { data } = await axios.get(`${BASE}/me/roadmap/replan-status`, { headers: auth() });
    return data;
  },

  // ── Placement readiness (Module 14) ──
  /** Three readiness figures, side by side. Deliberately never combined into one. */
  getPlacementReadiness: async (): Promise<PlacementReadinessView> => {
    const { data } = await axios.get(`${BASE}/me/placement-readiness`, { headers: auth() });
    return data;
  },
  /** What a role interview would cover, shown before the member commits to sitting one. */
  getInterviewCoverage: async (): Promise<InterviewCoverageView> => {
    const { data } = await axios.get(`${BASE}/me/interview/coverage`, { headers: auth() });
    return data;
  },

  // ── Rewards (Module 12) ──
  /** Catalogue plus this student's standing against each reward. */
  getRewards: async (): Promise<RewardCatalogue> => {
    const { data } = await axios.get(`${BASE}/me/rewards`, { headers: auth() });
    return data;
  },
  /** The intent token separates a real second redemption from a double-clicked button. */
  redeemReward: async (key: string, intentToken: string): Promise<any> => {
    const { data } = await axios.post(`${BASE}/me/rewards/${encodeURIComponent(key)}/redeem`,
      { intentToken }, { headers: auth() });
    return data;
  },
  getMyRedemptions: async (): Promise<{ redemptions: RedemptionRow[] }> => {
    const { data } = await axios.get(`${BASE}/me/redemptions`, { headers: auth() });
    return data;
  },
  /** Admin: the reward catalogue. */
  adminRewards: async (): Promise<{ rewards: AdminReward[]; policy: { minRedemption: number; expiryMonths: number } }> => {
    const { data } = await axios.get(`${BASE}/rewards/admin`, { headers: auth() });
    return data;
  },
  adminCreateReward: async (r: Partial<AdminReward>): Promise<{ reward: AdminReward }> => {
    const { data } = await axios.post(`${BASE}/rewards/admin`, r, { headers: auth() });
    return data;
  },
  adminUpdateReward: async (key: string, patch: Partial<AdminReward>): Promise<{ reward: AdminReward }> => {
    const { data } = await axios.put(`${BASE}/rewards/admin/${encodeURIComponent(key)}`, patch, { headers: auth() });
    return data;
  },
  adminRedemptions: async (status?: string, skip = 0): Promise<{ redemptions: AdminRedemption[]; total: number; counts: Record<string, number> }> => {
    const { data } = await axios.get(`${BASE}/rewards/admin/redemptions`, { headers: auth(), params: { ...(status ? { status } : {}), skip, limit: 50 } });
    return data;
  },
  adminFulfilRedemption: async (id: string, fulfillmentReference?: string, notes?: string): Promise<{ redemption: AdminRedemption }> => {
    const { data } = await axios.post(`${BASE}/rewards/admin/redemptions/${id}/fulfill`, { fulfillmentReference, notes }, { headers: auth() });
    return data;
  },
  adminCancelRedemption: async (id: string, reason?: string): Promise<{ redemption: AdminRedemption }> => {
    const { data } = await axios.post(`${BASE}/rewards/admin/redemptions/${id}/cancel`, { reason }, { headers: auth() });
    return data;
  },
  adminStrandedRedemptions: async (): Promise<{ stranded: AdminRedemption[] }> => {
    const { data } = await axios.get(`${BASE}/rewards/admin/stranded`, { headers: auth() });
    return data;
  },
  adminRecoverRedemption: async (id: string): Promise<{ ok: boolean; refused?: string }> => {
    const { data } = await axios.post(`${BASE}/rewards/admin/redemptions/${id}/recover`, {}, { headers: auth() });
    return data;
  },

  // ── Gamification (Module 11) ──
  /** XP, level, streak, badges and ranks. Read-only: no client can award anything. */
  getMyGamification: async (): Promise<GamificationSummary> => {
    const { data } = await axios.get(`${BASE}/me/gamification`, { headers: auth() });
    return data;
  },
  getMyXpHistory: async (limit = 30): Promise<{ entries: XpHistoryEntry[] }> => {
    const { data } = await axios.get(`${BASE}/me/gamification/xp-history`, { headers: auth(), params: { limit } });
    return data;
  },
  getMyLeaderboard: async (scope: string, period: string, limit?: number): Promise<ScopedLeaderboardResponse> => {
    const { data } = await axios.get(`${BASE}/me/leaderboard`, { headers: auth(), params: { scope, period, ...(limit ? { limit } : {}) } });
    return data;
  },
  /** Admin: XP rules, badges and leaderboard settings. */
  getAdminGamification: async (): Promise<AdminGamification> => {
    const { data } = await axios.get(`${BASE}/gamification/admin`, { headers: auth() });
    return data;
  },
  /** Admin: this month's figures for the XP & Rewards header. */
  getXpRewardsOverview: async (): Promise<XpRewardsOverview> => {
    const { data } = await axios.get(`${BASE}/gamification/admin/overview`, { headers: auth() });
    return data;
  },
  /** Admin: what each type of journey task pays. */
  updateTaskXp: async (byType: Record<string, number>): Promise<{ taskTypes: TaskXpType[] }> => {
    const { data } = await axios.put(`${BASE}/gamification/admin/task-xp`, { byType }, { headers: auth() });
    return data;
  },
  /** The XP price list the day player pays from — the Content Builder's "+N XP". */
  getTaskPrices: async (): Promise<TaskPrices> => {
    const { data } = await axios.get(`${BASE}/gamification/task-prices`, { headers: auth() });
    return data;
  },
  /** What one task pays, wherever it appears. */
  setTaskXpOverride: async (refKind: 'content' | 'module', refId: string, xp: number, title?: string): Promise<{ key: string; xp: number }> => {
    const { data } = await axios.put(`${BASE}/gamification/task-prices/override`, { refKind, refId, xp, title }, { headers: auth() });
    return data;
  },
  clearTaskXpOverride: async (refKind: 'content' | 'module', refId: string): Promise<void> => {
    await axios.delete(`${BASE}/gamification/task-prices/override`, { headers: auth(), params: { refKind, refId } });
  },
  updateXpRule: async (eventKey: string, patch: any): Promise<any> => {
    const { data } = await axios.put(`${BASE}/gamification/admin/rules/${eventKey}`, patch, { headers: auth() });
    return data;
  },
  updateBadgeDefinition: async (key: string, patch: Partial<BadgeInput>): Promise<{ badges: AdminBadgeRow[] }> => {
    const { data } = await axios.put(`${BASE}/gamification/admin/badges/${key}`, patch, { headers: auth() });
    return data;
  },
  createBadgeDefinition: async (body: BadgeInput): Promise<{ badges: AdminBadgeRow[] }> => {
    const { data } = await axios.post(`${BASE}/gamification/admin/badges`, body, { headers: auth() });
    return data;
  },
  /** Only a badge nobody has earned; the server refuses the rest. */
  deleteBadgeDefinition: async (key: string): Promise<{ badges: AdminBadgeRow[] }> => {
    const { data } = await axios.delete(`${BASE}/gamification/admin/badges/${key}`, { headers: auth() });
    return data;
  },
  /** The order members see the badges in. */
  reorderBadgeDefinitions: async (keys: string[]): Promise<{ badges: AdminBadgeRow[] }> => {
    const { data } = await axios.put(`${BASE}/gamification/admin/badges-order`, { keys }, { headers: auth() });
    return data;
  },
  /** Level names, level 1 first; [] goes back to the shipped names. */
  saveLevelNames: async (names: string[]): Promise<{ levels: LevelLadder }> => {
    const { data } = await axios.put(`${BASE}/gamification/admin/levels`, { names }, { headers: auth() });
    return data;
  },
  updateLeaderboardSettings: async (patch: any): Promise<any> => {
    const { data } = await axios.put(`${BASE}/gamification/admin/leaderboard`, patch, { headers: auth() });
    return data;
  },
  getRewardBudget: async (period?: string): Promise<RewardBudgetView> => {
    const { data } = await axios.get(`${BASE}/gamification/admin/reward-budget`, { headers: auth(), params: period ? { period } : {} });
    return data;
  },
  updateRewardBudget: async (patch: any): Promise<RewardBudgetView> => {
    const { data } = await axios.put(`${BASE}/gamification/admin/reward-budget`, patch, { headers: auth() });
    return data;
  },
  previewRewardBudget: async (body: any): Promise<any> => {
    const { data } = await axios.post(`${BASE}/gamification/admin/reward-budget/preview`, body, { headers: auth() });
    return data;
  },

  // ── Skill DNA (Module 7) ──
  /** The caller's own skills. `assessed: false` means not measured yet, not a score of 0. */
  getMySkillDna: async (): Promise<{ skills: SkillDnaRow[]; assessed: boolean }> => {
    const { data } = await axios.get(`${BASE}/me/skills`, { headers: auth() });
    return data;
  },
  /** Grades the open paper and projects it into skill evidence. */
  submitPersonalizedAssessment: async (answers: { sourceType: string; sourceId: string; response?: any }[]): Promise<any> => {
    const { data } = await axios.post(`${BASE}/me/assessment/personalized/submit`, { answers }, { headers: auth() });
    return data;
  },
  /** Admin: one member's full picture — account, CareerPilot setup, membership, payments, progress. */
  getMemberProfile: async (studentId: string): Promise<any> => {
    const { data } = await axios.get(`${BASE}/admin/students/${encodeURIComponent(studentId)}/profile`, { headers: auth() });
    return data.data;
  },
  /** Super admin: everything one member has done, newest first. `before` pages back in time. */
  getMemberActivity: async (studentId: string, q: { before?: string; kinds?: string } = {}): Promise<MemberActivity> => {
    const { data } = await axios.get(`${BASE}/admin/students/${encodeURIComponent(studentId)}/activity`, { headers: auth(), params: q });
    return data.data;
  },
  /** Admin: one member's Skill DNA. */
  getStudentSkillDna: async (studentId: string): Promise<{ skills: SkillDnaRow[]; assessed: boolean }> => {
    const { data } = await axios.get(`${BASE}/students/${studentId}/skills`, { headers: auth() });
    return data;
  },
  /** Admin: the observations behind one score, and the arithmetic applied. */
  explainStudentSkill: async (studentId: string, skillKey: string): Promise<any> => {
    const { data } = await axios.get(`${BASE}/students/${studentId}/skills/${skillKey}`, { headers: auth() });
    return data;
  },
  /** Admin recovery: recompute profiles from stored evidence. Idempotent. */
  rebuildStudentSkillDna: async (studentId: string): Promise<any> => {
    const { data } = await axios.post(`${BASE}/students/${studentId}/skills/rebuild`, {}, { headers: auth() });
    return data;
  },

  // ── Personalised assessment (Module 6) ──
  /** Runs the real generator and saves nothing — no attempt, no member data touched. */
  previewAssessment: async (studentId: string, attemptNumber = 1): Promise<AssessmentPreview> => {
    const { data } = await axios.post(`${BASE}/assessment/personalized/preview`, { studentId, attemptNumber }, { headers: auth() });
    return data;
  },
  assessmentPolicies: async (): Promise<{ policies: AssessmentPolicyRow[] }> => {
    const { data } = await axios.get(`${BASE}/assessment/personalized/policies`, { headers: auth() });
    return data;
  },

  // ── Assessment skill evidence (Module 5, admin) ──
  /** A page of content with its mappings. One evidence query per page, never per item. */
  listSkillEvidence: async (q: { sourceType: string; filter: string; search?: string; page: number }): Promise<{
    items: EvidenceItem[]; total: number; page: number; limit: number;
    sourceTypes: { key: string; label: string }[];
  }> => {
    const { data } = await axios.get(`${BASE}/skill-evidence`, { headers: auth(), params: q });
    return data;
  },
  /** Replaces an item's whole mapping — a skill left out is unmapped. */
  saveSkillEvidence: async (sourceType: string, sourceId: string, evidence: { skillKey: string; contribution: string; active: boolean }[]): Promise<{ item: any }> => {
    const { data } = await axios.put(`${BASE}/skill-evidence/${sourceType}/${encodeURIComponent(sourceId)}`, { evidence }, { headers: auth() });
    return data;
  },
  /** How much evidence exists per skill. Configuration completeness, not analytics. */
  skillEvidenceCoverage: async (): Promise<{ coverage: SkillCoverageRow[]; totals: any; sourceTypes: { key: string; label: string }[] }> => {
    const { data } = await axios.get(`${BASE}/skill-evidence/coverage`, { headers: auth() });
    return data;
  },
  /** Active, assessable skills — the only ones that may be newly mapped. */
  mappableSkills: async (): Promise<{ skills: MappableSkill[] }> => {
    const { data } = await axios.get(`${BASE}/skill-evidence/skills`, { headers: auth() });
    return data;
  },

  // ── Role skill blueprints (Module 4, admin) ──
  /** Every role with a count of what it expects — one query, no per-role fan-out. */
  listRoleBlueprints: async (): Promise<{ roles: BlueprintRoleRow[]; suggestedTaxonomyAdditions: string[] }> => {
    const { data } = await axios.get(`${BASE}/role-blueprints`, { headers: auth() });
    return data;
  },
  /** One blueprint, its skills resolved, plus the tree the picker needs. */
  getRoleBlueprint: async (roleKey: string): Promise<{ blueprint: ResolvedBlueprint; skillTree: SkillNode[]; vocabulary: any }> => {
    const { data } = await axios.get(`${BASE}/role-blueprints/${encodeURIComponent(roleKey)}`, { headers: auth() });
    return data;
  },
  /** Replaces the whole list — a requirement left out is removed. */
  saveRoleBlueprint: async (roleKey: string, requirements: BlueprintRequirement[]): Promise<{ blueprint: ResolvedBlueprint }> => {
    const { data } = await axios.put(`${BASE}/role-blueprints/${encodeURIComponent(roleKey)}`, { requirements }, { headers: auth() });
    return data;
  },
  publishRoleBlueprint: async (roleKey: string, published: boolean): Promise<{ blueprint: ResolvedBlueprint }> => {
    const { data } = await axios.post(`${BASE}/role-blueprints/${encodeURIComponent(roleKey)}/publish`, { published }, { headers: auth() });
    return data;
  },
  /** Idempotent, insert-only. `dryRun` reports without writing. */
  seedRoleBlueprints: async (dryRun: boolean): Promise<{ inserted: string[]; skipped: string[]; missingRoles: string[]; missingSkills: Record<string, string[]> }> => {
    const { data } = await axios.post(`${BASE}/role-blueprints/seed`, { dryRun }, { headers: auth() });
    return data;
  },

  // ── Skill graph (Module 3, admin) ──
  /** Tree, flat list and vocabulary in one response, so the pickers cannot disagree. */
  listSkills: async (): Promise<{
    tree: SkillNode[];
    skills: AdminSkill[];
    difficulties: string[];
    nodeTypes: string[];
    counts: { total: number; active: number; assessable: number; groups: number };
  }> => {
    const { data } = await axios.get(`${BASE}/skills`, { headers: auth() });
    return data;
  },
  createSkill: async (body: any): Promise<{ skill: AdminSkill }> => {
    const { data } = await axios.post(`${BASE}/skills`, body, { headers: auth() });
    return data;
  },
  updateSkill: async (id: string, body: any): Promise<{ skill: AdminSkill }> => {
    const { data } = await axios.put(`${BASE}/skills/${id}`, body, { headers: auth() });
    return data;
  },
  deleteSkill: async (id: string): Promise<{ success: boolean }> => {
    const { data } = await axios.delete(`${BASE}/skills/${id}`, { headers: auth() });
    return data;
  },
  /** Idempotent. `dryRun` reports what would be installed without writing. */
  seedSkills: async (dryRun: boolean): Promise<{ inserted: string[]; skipped: string[]; total: number }> => {
    const { data } = await axios.post(`${BASE}/skills/seed`, { dryRun }, { headers: auth() });
    return data;
  },

  // ── Career roles (Module 2, admin) ──
  listCareerRoles: async (): Promise<{
    domains: { key: string; label: string }[];
    roles: AdminCareerRole[];
    counts: { total: number; active: number; selectable: number };
  }> => {
    const { data } = await axios.get(`${BASE}/career-roles`, { headers: auth() });
    return data;
  },
  /** Counted on demand, not per row — see the admin screen's note. */
  careerRoleUsage: async (key: string): Promise<{ key: string; memberCount: number }> => {
    const { data } = await axios.get(`${BASE}/career-roles/${encodeURIComponent(key)}/usage`, { headers: auth() });
    return data;
  },
  createCareerRole: async (body: Partial<AdminCareerRole>): Promise<{ role: AdminCareerRole }> => {
    const { data } = await axios.post(`${BASE}/career-roles`, body, { headers: auth() });
    return data;
  },
  updateCareerRole: async (id: string, body: Partial<AdminCareerRole>): Promise<{ role: AdminCareerRole }> => {
    const { data } = await axios.put(`${BASE}/career-roles/${id}`, body, { headers: auth() });
    return data;
  },
  deleteCareerRole: async (id: string): Promise<{ success: boolean }> => {
    const { data } = await axios.delete(`${BASE}/career-roles/${id}`, { headers: auth() });
    return data;
  },

  // ── Career context (Module 1) ──
  /** The caller's own context. Derived values come from the server, never computed here. */
  getCareerContext: async (): Promise<{ context: CareerContext; options: CareerContextOptions }> => {
    const { data } = await axios.get(`${BASE}/me/context`, { headers: auth() });
    return data;
  },
  /** Partial save. Only `complete: true` marks onboarding finished. */
  updateCareerContext: async (patch: CareerContextPatch): Promise<{ context: CareerContext; options: CareerContextOptions }> => {
    const { data } = await axios.put(`${BASE}/me/context`, patch, { headers: auth() });
    return data;
  },

  /**
   * Can this member start a personalized assessment right now?
   *
   * Preflight so onboarding never offers a CTA that fails on click. All readiness rules
   * (blueprint published, skills configured, question pool) stay on the server — this is
   * a transport, not a second copy of the policy.
   */
  /** The welcome days, and how far this member has come through them. */
  getMyOrientation: async (): Promise<OrientationView> => {
    const { data } = await axios.get(`${BASE}/me/orientation`, { headers: auth() });
    return data;
  },
  /** One part of an orientation day, finished. Checklists send which lines are ticked. */
  completeOrientationItem: async (dayNumber: number, itemKey: string, checked?: number[]): Promise<{ ok: boolean; orientation: OrientationView }> => {
    const { data } = await axios.post(`${BASE}/me/orientation/item`, { dayNumber, itemKey, checked }, { headers: auth() });
    return data;
  },
  /** A whole orientation day. Refused, with what is outstanding, while a required part is unfinished. */
  completeOrientationDay: async (dayNumber: number): Promise<{ ok: boolean; message?: string; outstanding?: string[]; xpAwarded?: number; orientation?: OrientationView }> => {
    const { data } = await axios.post(`${BASE}/me/orientation/day`, { dayNumber }, { headers: auth() });
    return data;
  },
  /** The member's own recording of themselves, for a recording prompt. */
  uploadOrientationRecording: async (dayNumber: number, itemKey: string, blob: Blob, durationSec: number): Promise<{ ok: boolean; message?: string; orientation?: OrientationView }> => {
    const form = new FormData();
    form.append('recording', blob, `${itemKey}.webm`);
    form.append('dayNumber', String(dayNumber));
    form.append('itemKey', itemKey);
    form.append('durationSec', String(Math.round(durationSec)));
    const { data } = await axios.post(`${BASE}/me/orientation/recording`, form, { headers: auth() });
    return data;
  },
  orientationRecordingUrl: (dayNumber: number, itemKey: string): string =>
    `${BASE}/me/orientation/recording/${dayNumber}/${encodeURIComponent(itemKey)}`,

  /** Admin: the tenant's welcome, as it stands. */
  getOrientationProgram: async (): Promise<{ enabled: boolean; days: AuthoredOrientationDay[] }> => {
    const { data } = await axios.get(`${BASE}/orientation`, { headers: auth() });
    return data;
  },
  saveOrientationProgram: async (days: AuthoredOrientationDay[], enabled: boolean): Promise<{ ok: boolean; message?: string; enabled: boolean; days: AuthoredOrientationDay[] }> => {
    const { data } = await axios.put(`${BASE}/orientation`, { days, enabled }, { headers: auth() });
    return data;
  },
  resetOrientationProgram: async (): Promise<{ ok: boolean; enabled: boolean; days: AuthoredOrientationDay[] }> => {
    const { data } = await axios.post(`${BASE}/orientation/reset`, {}, { headers: auth() });
    return data;
  },

  /** May I test out of this topic? Every rule is the server's — this only asks. */
  placementCheckAvailability: async (day: number): Promise<PlacementAvailability> => {
    const { data } = await axios.get(`${BASE}/me/placement-check/day/${day}`, { headers: auth() });
    return data;
  },
  /**
   * Builds the paper and opens it. The taking screen then resumes it like any open paper, and its
   * submit is graded as a placement check on the server whichever screen sends it.
   */
  startPlacementCheck: async (day: number): Promise<{ ok: boolean; assessmentId?: string; questions?: number; message?: string }> => {
    const { data } = await axios.post(`${BASE}/me/placement-check/start`, { day }, { headers: auth() });
    return data;
  },
  getAssessmentAvailability: async (): Promise<AssessmentAvailability> => {
    const { data } = await axios.get(`${BASE}/me/assessment/personalized/availability`, { headers: auth() });
    return data;
  },

  // ── Assessment paper shape (admin) ──
  /** Every stage with its shipped defaults and this tenant's current values. */
  getEditablePolicies: async (): Promise<{ policies: EditablePolicy[]; bounds: PolicyBounds }> => {
    const { data } = await axios.get(`${BASE}/assessment/policies/editable`, { headers: auth() });
    return data;
  },
  saveEditablePolicies: async (policies: EditablePolicy[]): Promise<{ policies: EditablePolicy[]; bounds: PolicyBounds }> => {
    const { data } = await axios.put(`${BASE}/assessment/policies/editable`, { policies }, { headers: auth() });
    return data;
  },

  // ── AI question drafting (admin) ──
  /**
   * How much pool each assessable skill has, worst first. The number that matters is per
   * skill, not the total: a paper draws slots per skill, so a large pool concentrated on a
   * few skills still produces a repetitive paper.
   */

  // ── Question bank: the approved questions, browsable and editable ─────────
  //
  // Separate from question-drafts, which only ever lists what is still pending. Once a
  // draft was approved nothing could reach it again — targeting could not be changed and a
  // typo could not be fixed.
  listQuestionBank: async (q: QuestionBankQuery): Promise<QuestionBankPage> => {
    const { data } = await axios.get(`${BASE}/question-bank`, { params: q, headers: auth() });
    return data;
  },
  updateBankQuestion: async (
    sourceType: string, sourceId: string, body: QuestionBankEdit,
  ): Promise<{ success: boolean }> => {
    const { data } = await axios.put(`${BASE}/question-bank/${sourceType}/${sourceId}`, body, { headers: auth() });
    return data;
  },
  /** Take a borrowed LMS question into CareerPilot so it can be edited without touching the LMS. */
  copyBankQuestion: async (sourceId: string): Promise<{ success: boolean; sourceId: string }> => {
    const { data } = await axios.post(`${BASE}/question-bank/${sourceId}/copy`, {}, { headers: auth() });
    return data;
  },
  /** Apply one audience to many questions — the reason the screen exists. */
  bulkBankTargeting: async (
    targets: { sourceType: string; sourceId: string }[], audience: QuestionAudience,
  ): Promise<{ success: boolean; questions: number; mappings: number }> => {
    const { data } = await axios.post(`${BASE}/question-bank/targeting`, { targets, audience }, { headers: auth() });
    return data;
  },
  setBankActive: async (
    targets: { sourceType: string; sourceId: string }[], active: boolean,
  ): Promise<{ success: boolean; mappings: number }> => {
    const { data } = await axios.post(`${BASE}/question-bank/active`, { targets, active }, { headers: auth() });
    return data;
  },
  deleteBankQuestion: async (sourceType: string, sourceId: string): Promise<{ success: boolean }> => {
    const { data } = await axios.delete(`${BASE}/question-bank/${sourceType}/${sourceId}`, { headers: auth() });
    return data;
  },

  draftCoverage: async (): Promise<{ skills: PoolCoverageRow[] }> => {
    const { data } = await axios.get(`${BASE}/question-drafts/coverage`, { headers: auth() });
    return data;
  },
  listDrafts: async (q: { status?: string; skillKey?: string; page?: number; limit?: number } = {}):
    Promise<{ drafts: QuestionDraft[]; total: number; page: number; limit: number }> => {
    const { data } = await axios.get(`${BASE}/question-drafts`, { params: q, headers: auth() });
    return data;
  },
  generateDrafts: async (body: { skillKey: string; difficulty: string; count: number }):
    Promise<{ report: DraftBatchReport }> => {
    const { data } = await axios.post(`${BASE}/question-drafts/generate`, body, { headers: auth() });
    return data;
  },
  approveDraft: async (id: string, edits?: Partial<QuestionDraft>, note?: string): Promise<{ questionId: string }> => {
    const { data } = await axios.post(`${BASE}/question-drafts/${id}/approve`, { edits, note }, { headers: auth() });
    return data;
  },
  rejectDraft: async (id: string, note?: string): Promise<{ success: boolean }> => {
    const { data } = await axios.post(`${BASE}/question-drafts/${id}/reject`, { note }, { headers: auth() });
    return data;
  },
  /** Roles, years and courses a question may be aimed at. Admin-gated. */
  draftAudiences: async (): Promise<{
    roles: { key: string; label: string }[]; years: string[]; courses: string[]; branches?: string[];
  }> => {
    const { data } = await axios.get(`${BASE}/question-drafts/audiences`, { headers: auth() });
    return data;
  },
  /**
   * Approve a selection. Always resolves with a per-draft outcome — one refusal in the
   * middle must not discard the rest, and the reviewer needs to know which one it was.
   */
  approveDrafts: async (ids: string[]): Promise<{
    approved: string[]; failed: { id: string; message: string }[];
  }> => {
    const { data } = await axios.post(`${BASE}/question-drafts/approve-bulk`, { ids }, { headers: auth() });
    return data;
  },
  /** Write a question by hand. Goes live immediately — the author is the reviewer. */
  createManualQuestion: async (body: {
    skillKey: string; difficulty: string; question: string;
    options: { text: string; isCorrect: boolean }[];
    explanation?: string; codeSnippet?: string; language?: string;
    audienceRoles?: string[]; audienceYears?: string[]; audienceCourses?: string[];
  }): Promise<{ questionId: string; draftId: string }> => {
    const { data } = await axios.post(`${BASE}/question-drafts/manual`, body, { headers: auth() });
    return data;
  },

  // ── Pathway routing rules (who each pathway serves) ──
  /** Goals, stages, backgrounds and categories a rule may be written against. */
  ruleVocabulary: async (): Promise<RuleVocabulary> => {
    const { data } = await axios.get(`${BASE}/pathway-rules/vocabulary`, { headers: auth() });
    return data;
  },
  /** Dry-runs the SUBMITTED rules over every real member — nothing is saved. */
  previewRules: async (pathways: PassportPathway[]): Promise<RulePreview> => {
    const { data } = await axios.post(`${BASE}/pathway-rules/preview`, { pathways }, { headers: auth() });
    return data;
  },
  /** The diff only — who would move and where. Never writes. */
  reevaluatePathways: async (): Promise<ReevaluateResult> => {
    const { data } = await axios.post(`${BASE}/pathway-rules/reevaluate`, {}, { headers: auth() });
    return data;
  },
  /** Actually moves them. Separate permission (reroute_passport_members). */
  applyReevaluation: async (): Promise<ReevaluateResult> => {
    const { data } = await axios.post(`${BASE}/pathway-rules/reevaluate/apply`, {}, { headers: auth() });
    return data;
  },
  /** Plain-English audience → a rule block, for review in the editor. */
  draftRule: async (pathwayKey: string, audience: string): Promise<{ pathwayKey: string; match: PathwayMatch }> => {
    const { data } = await axios.post(`${BASE}/pathway-rules/draft`, { pathwayKey, audience }, { headers: auth() });
    return data;
  },

  // ── Drop-off funnel ──
  /** Stage definitions with live counts, plus totals. */
  getFunnel: async (): Promise<{ stages: FunnelStage[]; totals: FunnelTotals; notes: string[] }> => {
    const { data } = await axios.get(`${BASE}/funnel`, { headers: auth() });
    return data;
  },
  /** The people stuck in one stage, coldest first. */
  getFunnelStage: async (stage: string): Promise<{ stage: FunnelStage; total: number; returned: number; members: FunnelMember[] }> => {
    const { data } = await axios.get(`${BASE}/funnel/${stage}`, { headers: auth() });
    return data;
  },
  /**
   * Download the stage as CSV.
   *
   * Fetched rather than linked: the endpoint needs the Authorization header, and a plain
   * <a href> cannot send one — it would just 401. The response is turned into a blob and
   * handed to a temporary link so the browser saves it.
   */
  exportFunnelStage: async (stage: string, filename: string): Promise<void> => {
    const { data } = await axios.get(`${BASE}/funnel/${stage}/export`, {
      headers: auth(), responseType: 'blob',
    });
    const url = URL.createObjectURL(new Blob([data], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  },

  // Missions
  /** `day` omitted = today. Past days only; the server clamps anything ahead. */
  getToday: async (day?: number): Promise<TodayMissions> => {
    const { data } = await axios.get(`${BASE}/missions/today`, { headers: auth(), params: day ? { day } : undefined });
    return data;
  },
  /** `answer` is required for missions flagged needsAnswer — those have no surface to
   *  complete them on, so the written response IS the completion. */
  completeMission: async (key: string, answer?: string): Promise<{ ok: boolean; xp: number; streak: number; longestStreak: number; allDone: boolean; feedback?: string | null }> => {
    const { data } = await axios.post(`${BASE}/missions/complete`, { key, answer }, { headers: auth() });
    return data;
  },

  // ── Gamified member dashboard (one call for the whole home screen) ──
  getDashboard: async (): Promise<DashboardData> => {
    const { data } = await axios.get(`${BASE}/dashboard`, { headers: auth() });
    return data;
  },

  // ── Roadmap (full 90-day journey; free users get the 7-day preview) ──
  getRoadmap: async (): Promise<RoadmapResponse> => {
    const { data } = await axios.get(`${BASE}/roadmap`, { headers: auth() });
    return data;
  },

  // ── Practice Lab ──
  listPractice: async (params: { kind?: string; category?: string; source?: 'all' | 'builtin' | 'bank' } = {}): Promise<PracticeListResponse> => {
    const { data } = await axios.get(`${BASE}/practice`, { headers: auth(), params });
    return data;
  },
  getPractice: async (id: string): Promise<PracticeDetail> => {
    const { data } = await axios.get(`${BASE}/practice/${encodeURIComponent(id)}`, { headers: auth() });
    return data;
  },
  /**
   * A nudge about the member's OWN code, as opposed to the written hints which are the same
   * for everyone. Counts toward hintsUsed and reduces the XP a solve pays.
   */
  practiceAiHint: async (id: string, code: string): Promise<{ hint: string }> => {
    const { data } = await axios.post(`${BASE}/practice/${encodeURIComponent(id)}/ai-hint`, { code }, { headers: auth() });
    return data;
  },
  runPractice: async (id: string, code: string, language: string): Promise<RunOutcome> => {
    const { data } = await axios.post(`${BASE}/practice/${encodeURIComponent(id)}/run`, { code, language }, { headers: auth() });
    return data;
  },
  submitPractice: async (id: string, body: { code?: string; language?: string; answers?: number[] }): Promise<SubmitOutcome> => {
    const { data } = await axios.post(`${BASE}/practice/${encodeURIComponent(id)}/submit`, body, { headers: auth() });
    return data;
  },

  // ── Mock interviews ──
  listInterviews: async (): Promise<{
    locked?: boolean; priceInr?: number; aiAvailable?: boolean;
    sessions?: InterviewSession[]; openSessionId?: string | null;
    /** The member's plan — the rounds they were given and what is left of their allowance. */
    entitlement?: MemberEntitlement;
  }> => {
    const { data } = await axios.get(`${BASE}/interview`, { headers: auth() });
    return data;
  },
  /** `companySlug` primes the interviewer with that company's rounds and most-asked topics. */
  /**
   * Open a mock interview.
   *
   * `mode: 'role'` builds the paper from the member's own role blueprint, which is the only
   * kind of sitting that can later become skill evidence. The member never names the skills
   * — coverage is resolved server-side.
   */
  /**
   * `round` is a key from the member's own entitlement — it picks one of the rounds their
   * plan gives them, and the server rejects a key that is not on it. Passing none is the
   * old behaviour: an untyped mock of the built-in shape.
   */
  startInterview: async (
    companySlug?: string, mode?: 'role' | 'intro', round?: string,
  ): Promise<{ session: InterviewSession; resumed?: boolean; aiAvailable?: boolean; mismatched?: boolean }> => {
    const body: any = {};
    if (companySlug) body.companySlug = companySlug;
    if (mode) body.mode = mode;
    if (round) body.round = round;
    const { data } = await axios.post(`${BASE}/interview/start`, body, { headers: auth() });
    return data;
  },
  interviewTurn: async (id: string, answer: string): Promise<{ say: string; kind: string; endInterview: boolean; session: InterviewSession }> => {
    const { data } = await axios.post(`${BASE}/interview/${id}/turn`, { answer }, { headers: auth() });
    return data;
  },
  /**
   * Close and grade an interview.
   *
   * `finalizing: true` means another request already owns the grading — a double-tapped
   * button, or a retry after a timeout. It is not an error and the session is not lost; ask
   * again in a moment and the graded result comes back.
   */
  finishInterview: async (id: string): Promise<{
    session: InterviewSession; scored?: boolean; finalizing?: boolean;
  }> => {
    const { data } = await axios.post(`${BASE}/interview/${id}/finish`, {}, { headers: auth() });
    return data;
  },
  /**
   * Store the session video. Deliberately separate from finishInterview: the interview is
   * already graded and paid for by then, so a failed upload costs the recording and nothing
   * else. Callers should treat a rejection as a warning, never as a lost interview.
   */
  uploadInterviewRecording: async (id: string, blob: Blob, durationSec: number): Promise<{ ok: boolean }> => {
    const fd = new FormData();
    fd.append('recording', blob, 'interview.webm');
    fd.append('durationSec', String(Math.round(durationSec)));
    const { data } = await axios.post(`${BASE}/interview/${id}/recording`, fd, {
      headers: { ...auth(), 'Content-Type': 'multipart/form-data' },
      timeout: 300000,
    });
    return data;
  },
  /**
   * A playable URL for a stored recording. Fetched with the auth header and turned into a
   * blob URL, because a <video src> cannot carry one and the bucket is private — there is no
   * public link to point at. The caller must revoke the URL.
   */
  interviewRecordingUrl: async (id: string): Promise<string> => {
    const res = await fetch(`${BASE}/interview/${id}/recording`, { headers: auth() as any });
    if (!res.ok) throw new Error('Could not load the recording');
    return URL.createObjectURL(await res.blob());
  },
  /** The interviewer's line as real spoken audio. Returns a blob URL the caller must revoke. */
  speakInterviewLine: async (text: string): Promise<string> => {
    const { data } = await axios.post(`${BASE}/interview/speak`, { text }, { headers: auth(), responseType: 'blob' });
    return URL.createObjectURL(data as Blob);
  },

  /** Import an existing CV. Parsed fields fill gaps only — they never overwrite typed work. */
  importResume: async (file: File): Promise<{ sections: ResumeSections; importedChars: number }> => {
    const fd = new FormData();
    fd.append('file', file);
    const { data } = await axios.post(`${BASE}/resume/import`, fd, { headers: auth() });
    return data;
  },

  // ── Company preparation (Module 15) ──
  /**
   * How ready the member is for THIS company, alongside eligibility and their role figure.
   *
   * Four separate numbers, deliberately never combined. `fit.readiness` is null when nothing
   * of theirs has been measured against this company's requirements — render that as "not
   * assessed yet", never as 0%.
   */
  companyReadiness: async (slug: string): Promise<CompanyReadinessView> => {
    const { data } = await axios.get(`${BASE}/companies/${slug}/readiness`, { headers: auth() });
    return data;
  },
  companyPreparation: async (slug: string): Promise<CompanyPreparationView> => {
    const { data } = await axios.get(`${BASE}/companies/${slug}/preparation`, { headers: auth() });
    return data;
  },
  companyOverview: async (): Promise<CompanyOverviewView> => {
    const { data } = await axios.get(`${BASE}/company-prep/overview`, { headers: auth() });
    return data;
  },
  setCompanyTargets: async (slugs: string[], primary?: string): Promise<{
    targets: { slug: string; primary: boolean }[]; maxTargets: number; rejected?: string[]; message?: string;
  }> => {
    const { data } = await axios.put(`${BASE}/company-prep/targets`, { slugs, primary }, { headers: auth() });
    return data;
  },

  // ── CareerPilot analytics + health (admin) ──
  /**
   * Every analytics call takes the SAME bounded range the server accepts and returns it in
   * the response. The client never invents a window the API would reject, and never
   * computes a metric of its own — readiness lives on the server.
   */
  analytics: async (
    section: 'overview' | 'skills' | 'progress' | 'engagement' | 'economy' | 'placement',
    range: { from?: string; to?: string } = {},
  ): Promise<AnalyticsEnvelope> => {
    const { data } = await axios.get(`${BASE}/admin/analytics/${section}`, { headers: auth(), params: range });
    return data;
  },
  configHealth: async (): Promise<ConfigHealthView> => {
    const { data } = await axios.get(`${BASE}/admin/health/configuration`, { headers: auth() });
    return data;
  },
  launchReadiness: async (): Promise<LaunchReadinessView> => {
    const { data } = await axios.get(`${BASE}/admin/health/launch-readiness`, { headers: auth() });
    return data;
  },

  // ── Company preparation profiles (admin) ──
  companyProfiles: async (slug: string): Promise<CompanyProfileAdmin> => {
    const { data } = await axios.get(`${BASE}/company-admin/${slug}/profiles`, { headers: auth() });
    return data;
  },
  saveCompanyProfile: async (slug: string, roleKey: string, body: any): Promise<{ profile: CompanyProfileRow }> => {
    const { data } = await axios.put(`${BASE}/company-admin/${slug}/profiles/${roleKey}`, body, { headers: auth() });
    return data;
  },
  publishCompanyProfile: async (slug: string, roleKey: string, profileId: string): Promise<{ profile: CompanyProfileRow }> => {
    const { data } = await axios.post(`${BASE}/company-admin/${slug}/profiles/${roleKey}/publish`, { profileId }, { headers: auth() });
    return data;
  },

  // ── Company Questions ──
  listCompanies: async (): Promise<{ locked?: boolean; priceInr?: number; companyTypes?: TaxItem[]; companies?: CompanyRow[] }> => {
    const { data } = await axios.get(`${BASE}/companies`, { headers: auth() });
    return data;
  },
  companyDetail: async (slug: string, params: Record<string, string> = {}): Promise<CompanyDetail> => {
    const { data } = await axios.get(`${BASE}/companies/${slug}`, { headers: auth(), params });
    return data;
  },
  submitExperience: async (slug: string, body: any): Promise<{ success: boolean; message: string }> => {
    const { data } = await axios.post(`${BASE}/companies/${slug}/experience`, body, { headers: auth() });
    return data;
  },
  listExperiences: async (status = 'pending'): Promise<{ experiences: AdminExperience[] }> => {
    const { data } = await axios.get(`${BASE}/company-admin/experiences`, { headers: auth(), params: { status } });
    return data;
  },
  moderateExperience: async (id: string, status: 'published' | 'rejected'): Promise<any> => {
    const { data } = await axios.put(`${BASE}/company-admin/experiences/${id}`, { status }, { headers: auth() });
    return data;
  },
  contributeQuestion: async (slug: string, body: any): Promise<{ success: boolean; message: string }> => {
    const { data } = await axios.post(`${BASE}/companies/${slug}/contribute`, body, { headers: auth() });
    return data;
  },
  // Admin
  getCompanyAdmin: async (): Promise<CompanyAdmin> => {
    const { data } = await axios.get(`${BASE}/company-admin`, { headers: auth() });
    return data;
  },
  saveTaxonomy: async (body: any): Promise<any> => {
    const { data } = await axios.put(`${BASE}/company-admin/taxonomy`, body, { headers: auth() });
    return data;
  },
  saveCompany: async (body: any, id?: string): Promise<any> => {
    const url = id ? `${BASE}/company-admin/companies/${id}` : `${BASE}/company-admin/companies`;
    const { data } = id
      ? await axios.put(url, body, { headers: auth() })
      : await axios.post(url, body, { headers: auth() });
    return data;
  },
  deleteCompany: async (id: string): Promise<any> => {
    const { data } = await axios.delete(`${BASE}/company-admin/companies/${id}`, { headers: auth() });
    return data;
  },
  adminQuestions: async (slug: string, status?: string): Promise<{ questions: AdminQuestion[] }> => {
    const { data } = await axios.get(`${BASE}/company-admin/${slug}/questions`, { headers: auth(), params: status ? { status } : undefined });
    return data;
  },
  saveQuestions: async (slug: string, questions: any[]): Promise<{ added: number }> => {
    const { data } = await axios.post(`${BASE}/company-admin/${slug}/questions`, { questions }, { headers: auth() });
    return data;
  },
  importQuestions: async (slug: string, raw: string): Promise<{ parsed: any[] }> => {
    const { data } = await axios.post(`${BASE}/company-admin/${slug}/import`, { raw }, { headers: auth() });
    return data;
  },
  predictQuestions: async (slug: string, body: any): Promise<{ parsed: any[] }> => {
    const { data } = await axios.post(`${BASE}/company-admin/${slug}/predict`, body, { headers: auth() });
    return data;
  },
  updateQuestion: async (id: string, body: any): Promise<any> => {
    const { data } = await axios.put(`${BASE}/company-admin/questions/${id}`, body, { headers: auth() });
    return data;
  },
  deleteQuestion: async (id: string): Promise<any> => {
    const { data } = await axios.delete(`${BASE}/company-admin/questions/${id}`, { headers: auth() });
    return data;
  },

  // ── Company mock test ──
  startMockTest: async (slug: string): Promise<{ attempt: MockAttempt; resumed?: boolean; generated?: number; banked?: number }> => {
    const { data } = await axios.post(`${BASE}/companies/${slug}/mock-test/start`, {}, { headers: auth() });
    return data;
  },
  getMockTest: async (id: string): Promise<{ attempt: MockAttempt; result: MockResult | null }> => {
    const { data } = await axios.get(`${BASE}/mock-test/${id}`, { headers: auth() });
    return data;
  },
  saveMockAnswer: async (id: string, questionId: string, chosen: number): Promise<any> => {
    const { data } = await axios.put(`${BASE}/mock-test/${id}/answer`, { questionId, chosen }, { headers: auth() });
    return data;
  },
  submitMockTest: async (id: string): Promise<{ result: MockResult }> => {
    const { data } = await axios.post(`${BASE}/mock-test/${id}/submit`, {}, { headers: auth() });
    return data;
  },
  mockTestHistory: async (slug: string): Promise<{ attempts: any[] }> => {
    const { data } = await axios.get(`${BASE}/companies/${slug}/mock-test/history`, { headers: auth() });
    return data;
  },

  // ── Company roster pipeline ──
  bulkCreateCompanies: async (names: string, type: string): Promise<{ created: number; skipped: number }> => {
    const { data } = await axios.post(`${BASE}/company-admin/bulk`, { names, type }, { headers: auth() });
    return data;
  },
  readinessBoard: async (): Promise<{ rows: ReadinessRow[]; liveCount: number; total: number }> => {
    const { data } = await axios.get(`${BASE}/company-admin/readiness`, { headers: auth() });
    return data;
  },
  draftProfile: async (slug: string): Promise<{ drafted: boolean; patternRounds: number; readiness: any }> => {
    const { data } = await axios.post(`${BASE}/company-admin/${slug}/draft-profile`, {}, { headers: auth() });
    return data;
  },
  verifyFields: async (slug: string, body: { eligibility?: boolean; salary?: boolean }): Promise<any> => {
    const { data } = await axios.put(`${BASE}/company-admin/${slug}/verify`, body, { headers: auth() });
    return data;
  },
  savePattern: async (slug: string, rounds: any[], role = ''): Promise<any> => {
    const { data } = await axios.put(`${BASE}/company-admin/${slug}/pattern`, { rounds, role }, { headers: auth() });
    return data;
  },

  // ── Tech News ──
  getNews: async (): Promise<{ locked?: boolean; priceInr?: number; items?: NewsItem[] }> => {
    const { data } = await axios.get(`${BASE}/news`, { headers: auth() });
    return data;
  },
  listNewsAdmin: async (): Promise<{ items: AdminNewsItem[]; hoursSincePublish: number | null }> => {
    const { data } = await axios.get(`${BASE}/news/admin`, { headers: auth() });
    return data;
  },
  /** Paste a link — the server fetches it and the AI writes a draft. Nothing is saved. */
  draftNews: async (url: string): Promise<{ draft: NewsDraft }> => {
    const { data } = await axios.post(`${BASE}/news/admin/draft`, { url }, { headers: auth() });
    return data;
  },
  createNews: async (body: Partial<AdminNewsItem>): Promise<{ item: AdminNewsItem }> => {
    const { data } = await axios.post(`${BASE}/news/admin`, body, { headers: auth() });
    return data;
  },
  updateNews: async (id: string, body: Partial<AdminNewsItem>): Promise<{ item: AdminNewsItem }> => {
    const { data } = await axios.put(`${BASE}/news/admin/${id}`, body, { headers: auth() });
    return data;
  },
  deleteNews: async (id: string): Promise<{ success: boolean }> => {
    const { data } = await axios.delete(`${BASE}/news/admin/${id}`, { headers: auth() });
    return data;
  },

  // ── Leaderboard ──

  // ── My profile ──
  getMyProfile: async (): Promise<{ profile: MemberProfile }> => {
    const { data } = await axios.get(`${BASE}/me/profile`, { headers: auth() });
    return data;
  },
  updateMyProfile: async (p: MemberProfile): Promise<{ profile: MemberProfile }> => {
    const { data } = await axios.put(`${BASE}/me/profile`, p, { headers: auth() });
    return data;
  },

  // ── Coins ──
  getCoins: async (): Promise<CoinsResponse> => {
    const { data } = await axios.get(`${BASE}/coins`, { headers: auth() });
    return data;
  },
  /** Turn XP into coins. `coins` omitted: as many as possible. XP, level and rank never go down. */
  convertXpToCoins: async (coins?: number): Promise<{ coins: number; xpUsed: number; balance: number; conversion: XpConversionStatus }> => {
    const { data } = await axios.post(`${BASE}/coins/convert`, coins === undefined ? {} : { coins }, { headers: auth() });
    return data;
  },
  getCoinAdmin: async (): Promise<CoinAdminResponse> => {
    const { data } = await axios.get(`${BASE}/coins/admin`, { headers: auth() });
    return data;
  },
  saveCoinConfig: async (patch: Partial<CoinConfig>): Promise<{ config: CoinConfig }> => {
    const { data } = await axios.put(`${BASE}/coins/admin/config`, patch, { headers: auth() });
    return data;
  },
  saveCoinRules: async (rules: CoinRule[]): Promise<{ rules: CoinRule[] }> => {
    const { data } = await axios.put(`${BASE}/coins/admin/rules`, { rules }, { headers: auth() });
    return data;
  },
  getCoinLedger: async (): Promise<{ entries: CoinLedgerRow[] }> => {
    const { data } = await axios.get(`${BASE}/coins/admin/ledger`, { headers: auth() });
    return data;
  },

  // ── Resume Center ──
  getResume: async (): Promise<{ resume: { sections: ResumeSections; score: ResumeScore | null; scoredAt?: string; version: number } }> => {
    const { data } = await axios.get(`${BASE}/resume`, { headers: auth() });
    return data;
  },
  saveResume: async (sections: ResumeSections): Promise<{ resume: { sections: ResumeSections; score: ResumeScore | null } }> => {
    const { data } = await axios.put(`${BASE}/resume`, { sections }, { headers: auth() });
    return data;
  },
  scoreResume: async (): Promise<{ score: ResumeScore; xpAwarded: number; atsReady: boolean; goodScore: number }> => {
    const { data } = await axios.post(`${BASE}/resume/score`, {}, { headers: auth() });
    return data;
  },
  improveResume: async (): Promise<{ sections: ResumeSections }> => {
    const { data } = await axios.post(`${BASE}/resume/improve`, {}, { headers: auth() });
    return data;
  },

  // ── Admin content (Pathways + Mission pools) ──
  // ── Concept bank ──
  listConcepts: async (): Promise<{ concepts: ConceptRow[]; summary: ConceptSummary }> => {
    const { data } = await axios.get(`${BASE}/skill-resources/concepts`, { headers: auth() });
    return data;
  },
  audienceOptions: async (): Promise<AudienceOptions> => {
    const { data } = await axios.get(`${BASE}/skill-resources/audience-options`, { headers: auth() });
    return data;
  },
  listMaterials: async (skillKey: string): Promise<{
    resources: MaterialRow[]; workTypes: string[]; resourceTypes: string[]; materialTypes: string[];
  }> => {
    const { data } = await axios.get(`${BASE}/skill-resources`, {
      headers: auth(), params: { skillKey },
    });
    return data;
  },
  createMaterial: async (payload: any): Promise<{ resource: MaterialRow }> => {
    const { data } = await axios.post(`${BASE}/skill-resources`, payload, { headers: auth() });
    return data;
  },
  updateMaterial: async (id: string, payload: any): Promise<{ resource: MaterialRow }> => {
    const { data } = await axios.put(`${BASE}/skill-resources/${id}`, payload, { headers: auth() });
    return data;
  },
  deleteMaterial: async (id: string): Promise<{ deleted: boolean }> => {
    const { data } = await axios.delete(`${BASE}/skill-resources/${id}`, { headers: auth() });
    return data;
  },

  uploadAttachment: async (file: File): Promise<{ attachment: MaterialAttachment }> => {
    const form = new FormData();
    form.append('file', file);
    // Content-Type is left to the browser on purpose: setting it by hand drops the
    // multipart boundary and the server receives a body it cannot parse.
    const { data } = await axios.post(`${BASE}/skill-resources/attachments`, form, { headers: auth() });
    return data;
  },

  /**
   * A URL the browser can stream one attachment from.
   *
   * Not a blob: an attachment may be up to 1GB, and fetching it through axios would
   * assemble the whole thing in browser memory before a single byte was shown. A real URL
   * lets the browser stream, resume and hand off to its own PDF or video viewer.
   *
   * The credential is a ten-minute ticket naming this one file, NOT the session JWT — a
   * JWT here would be written into access logs, browser history and Referer headers.
   */
  /** Member: one material an admin wrote, with its audience re-checked server-side. */
  getMemberMaterial: async (id: string): Promise<any> => {
    const { data } = await axios.get(`${BASE}/me/material/${id}`, { headers: auth() });
    return data;
  },

  /**
   * The whole course for one skill: its topics, its subtopics, and where the student is.
   *
   * Returns null on 404, which is the ordinary case rather than an error — most skills have no
   * authored journey yet, and a screen that showed a red box for that would be shouting about
   * content the student was never promised.
   */
  /**
   * This student's ninety days, or why they do not have them yet.
   *
   * The unavailable case is an ordinary 200, not an error: a curriculum still being written is a
   * true state of the product, and the screen renders it rather than throwing.
   */
  getMySpine: async (): Promise<SpineOutcome> => {
    const { data } = await axios.get(`${BASE}/me/spine`, { headers: auth() });
    return data;
  },

  /** For the people authoring it. How much of the spine exists, band by band. */
  getSpineCoverage: async (q: { language?: string; direction?: string } = {}): Promise<SpineCoverage> => {
    const { data } = await axios.get(`${BASE}/spine/coverage`, { headers: auth(), params: q });
    return data;
  },

  getMyConceptJourney: async (skillKey: string): Promise<ConceptJourney | null> => {
    try {
      const { data } = await axios.get(
        `${BASE}/me/concept-journey/${encodeURIComponent(skillKey)}`, { headers: auth() });
      return data;
    } catch (e: any) {
      if (e?.response?.status === 404) return null;
      throw e;
    }
  },

  attachmentUrl: async (fileKey: string): Promise<string> => {
    const [folder, name] = fileKey.split('/');
    const { data } = await axios.post(
      `${BASE}/skill-resources/attachment-token`, { folder, name }, { headers: auth() });
    return `${BASE}/skill-resources/attachment-file/${folder}/${name}?t=${encodeURIComponent(data.token)}`;
  },

  getContent: async (): Promise<{ content: PassportContentDoc; categories: { key: string; label: string; weight: number }[] }> => {
    const { data } = await axios.get(`${BASE}/content`, { headers: auth() });
    return data;
  },
  saveContent: async (patch: Partial<PassportContentDoc>): Promise<{ content: PassportContentDoc }> => {
    const { data } = await axios.put(`${BASE}/content`, patch, { headers: auth() });
    return data;
  },
  resetContent: async (what: 'all' | 'pathways' | 'missions'): Promise<{ content: PassportContentDoc }> => {
    const { data } = await axios.post(`${BASE}/content/reset`, { what }, { headers: auth() });
    return data;
  },
  previewContent: async (body: Partial<PassportContentDoc> & { pathway?: string }): Promise<ContentPreview> => {
    const { data } = await axios.post(`${BASE}/content/preview`, body, { headers: auth() });
    return data;
  },

  // Membership checkout (₹499). Opens Razorpay and resolves true on successful activation.
  membershipCheckout: async (): Promise<{ ok: boolean; message?: string; testMode?: boolean }> => {
    // Every caller's success is celebrated here, once, whichever screen the member paid from.
    const r = await passportApi.membershipCheckoutRaw();
    if (r.ok) celebrateMembership();
    return r;
  },

  membershipCheckoutRaw: async (): Promise<{ ok: boolean; message?: string; testMode?: boolean }> => {
    /**
     * The order is requested BEFORE Razorpay's script is loaded, because in test mode there is
     * no Razorpay: loading it first would fail the whole checkout on a connection that is only
     * needed for the live path.
     */
    let order: any;
    try {
      /**
       * Sent so a member who arrived again through a NEW campaign has that recorded against the
       * order that followed it. The server still reads first touch from the account, so this can
       * only ever add a last touch — a client cannot claim an introduction it did not produce.
       */
      const { data } = await axios.post(
        `${BASE}/membership/order`,
        { attribution: attributionForSubmit() },
        { headers: auth() },
      );
      order = data;
    } catch (e: any) {
      return { ok: false, message: e?.response?.data?.message || 'Could not start payment.' };
    }

    /* Test mode — the server took no money and created no Razorpay order, so there is nothing
       to open. Completing is a single call, and it refuses if the tenant has since gone live. */
    if (order?.testMode) {
      try {
        await axios.post(`${BASE}/membership/test-complete`, { orderId: order.orderId }, { headers: auth() });
        return { ok: true, testMode: true };
      } catch (e: any) {
        return { ok: false, message: e?.response?.data?.message || 'Could not complete the test payment.' };
      }
    }

    const ready = await loadRazorpay();
    if (!ready) return { ok: false, message: 'Could not load the payment window. Check your connection.' };
    // Absolute return URL for redirect-mode checkout (mobile / incognito / popup-blocked,
    // where the in-page handler can't fire). Razorpay redirects here after payment; our
    // server settles and bounces back to /passport.
    const apiRoot = process.env.REACT_APP_API_URL || '/api/v1';
    const base = apiRoot.startsWith('http') ? apiRoot : window.location.origin + apiRoot;
    // `origin`: come back to the address the member is on, not the server's configured one —
    // on a LAN dev machine that one goes stale with every new IP (server checks it is trusted).
    const callbackUrl = `${base}/payments/return?to=${encodeURIComponent('/careerpilot?paid=1')}&origin=${encodeURIComponent(window.location.origin)}`;
    return new Promise((resolve) => {
      const rzp = new (window as any).Razorpay({
        key: order.keyId, amount: order.amount, currency: order.currency,
        name: order.name, description: order.description, order_id: order.orderId,
        prefill: order.prefill, theme: { color: '#6650d8' },
        callback_url: callbackUrl, redirect: true,
        handler: async (resp: any) => {
          try {
            await axios.post(`${BASE}/membership/verify`, {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            }, { headers: auth() });
            resolve({ ok: true });
          } catch (e: any) { resolve({ ok: false, message: e?.response?.data?.message || 'Verification failed.' }); }
        },
        // The payment can complete in a redirected tab, so the handler above never fires
        // in this window. On dismiss, re-check server state (the webhook may have already
        // activated the membership) before treating it as a cancellation.
        modal: {
          ondismiss: async () => {
            try { const me = await passportApi.me(); if (me?.active) return resolve({ ok: true }); } catch { /* ignore */ }
            resolve({ ok: false, message: 'Payment cancelled.' });
          },
        },
      });
      rzp.on('payment.failed', (r: any) => resolve({ ok: false, message: r?.error?.description || 'Payment failed.' }));
      rzp.open();
    });
  },

  // ── Mock interview plans (admin) ──
  listInterviewPlans: async (): Promise<InterviewPlansResponse> => {
    const { data } = await axios.get(`${BASE}/interview-plans`, { headers: auth() });
    return data;
  },
  createInterviewPlan: async (payload: InterviewPlanInput): Promise<{ plan: InterviewPlan }> => {
    const { data } = await axios.post(`${BASE}/interview-plans`, payload, { headers: auth() });
    return data;
  },
  updateInterviewPlan: async (id: string, payload: InterviewPlanInput): Promise<{ plan: InterviewPlan }> => {
    const { data } = await axios.put(`${BASE}/interview-plans/${id}`, payload, { headers: auth() });
    return data;
  },
  deleteInterviewPlan: async (id: string): Promise<void> => {
    await axios.delete(`${BASE}/interview-plans/${id}`, { headers: auth() });
  },
  reorderInterviewPlans: async (ids: string[]): Promise<{ plans: InterviewPlan[]; warnings: PlanWarning[] }> => {
    const { data } = await axios.put(`${BASE}/interview-plans/reorder`, { ids }, { headers: auth() });
    return data;
  },
  previewInterviewPlan: async (studentId: string): Promise<InterviewPlanPreview> => {
    const { data } = await axios.get(`${BASE}/interview-plans/preview`, { headers: auth(), params: { studentId } });
    return data;
  },

};

// ── Mock interview plans ──

export type InterviewRoundType = 'technical' | 'hr' | 'communication';

export interface InterviewRound {
  type: InterviewRoundType;
  label: string;
  questions: number;
  minutes: number;
}

/** The shared targeting shape — same five axes the concept and question banks use. */
export interface PlanAudience {
  years: string[]; courses: string[]; branches: string[]; roles: string[]; stages: string[];
}

export const emptyPlanAudience = (): PlanAudience => ({
  years: [], courses: [], branches: [], roles: [], stages: [],
});

export interface InterviewPlan {
  id: string;
  name: string;
  active: boolean;
  fallback: boolean;
  priority: number;
  audience: PlanAudience;
  rounds: InterviewRound[];
  quota: { perThirtyDays: number; cooldownHours: number };
  notes: string;
  totals: { questions: number; minutes: number };
  /** How many members this plan actually WINS — not how many match its audience. */
  members?: number;
}

export type InterviewPlanInput = Omit<InterviewPlan, 'id' | 'totals' | 'members'>;

export interface PlanWarning { level: 'warn' | 'info'; planId?: string; message: string }

export interface PlanBounds {
  /** Per ROUND, because each round is its own sitting and is graded on its own transcript. */
  questionsPerRound: { min: number; max: number };
  minutesPerRound: { min: number; max: number };
  rounds: { min: number; max: number };
  perThirtyDays: { min: number; max: number };
  cooldownHours: { min: number; max: number };
  priority: { min: number; max: number };
}

export interface InterviewPlansResponse {
  plans: InterviewPlan[];
  warnings: PlanWarning[];
  totals: { members: number; onDefault: number };
  /** Measured from the AiUsage ledger. `perInterviewInr` is null when the sample is too small. */
  cost: { perInterviewInr: number | null; sample: number; windowDays: number };
  bounds: PlanBounds;
  roundTypes: { key: InterviewRoundType; label: string }[];
  defaultShape: { rounds: InterviewRound[]; totals: { questions: number; minutes: number } };
}

export interface PlanTraceRow {
  id: string; name: string; priority: number; fallback: boolean; matched: boolean; reason: string;
}

/**
 * One interview the member has been given — the student-side view of an admin's plan round.
 *
 * `used` is inside the same rolling 30-day window the allowance is counted over, so the two
 * numbers on the screen always describe the same period.
 */
export interface MemberRoundView {
  key: string;
  type: InterviewRoundType;
  /** Always set — the kind of interview this is ("Technical Interview"). */
  title: string;
  /** The admin's own name for it, or empty. A subtitle, not the title. */
  label: string;
  questions: number;
  minutes: number;
  used: number;
  lastSatAt: string | null;
}

export interface MemberEntitlement {
  planId: string | null;
  planName: string | null;
  rounds: MemberRoundView[];
  /** 0 means unlimited. */
  perThirtyDays: number;
  used: number;
  /** Null when unlimited. */
  remaining: number | null;
  cooldownHours: number;
  nextAvailableAt: string | null;
  windowResetsAt: string | null;
  canStart: boolean;
  blockedReason: string;
}

export interface InterviewPlanPreview {
  student: { id: string; name: string; year: string; course: string; branch: string; role: string; stage: string };
  plan: InterviewPlan | null;
  rounds: InterviewRound[];
  quota: { perThirtyDays: number; cooldownHours: number };
  totals: { questions: number; minutes: number };
  trace: PlanTraceRow[];
}

// ── Gamified dashboard ──
export interface LevelInfo {
  level: number; title: string; xp: number;
  xpIntoLevel: number; xpForThisLevel: number; xpToNextLevel: number;
  nextLevel: number; nextTitle: string; progressPct: number;
}
/** A member's badge. `icon` is a Bootstrap Icons class ("bi-mic-fill"). */
export interface Badge { key: string; label: string; icon: string; color: string; hint: string; earned: boolean; progress: number; earnedAt?: string | null }
export interface DashboardData {
  /**
   * The daily plan's own outcome, including WHY it is unavailable. The dashboard used to
   * render a bare "No missions generated for today", which is true and useless: the member
   * is not told that the plan needs generating, nor given the button that does it.
   */
  dailyPlan?: { available: boolean; reason?: string; message?: string };
  active: boolean;
  /** Which day of the journey the member is on. */
  day?: number;
  /** Null when the coin ledger is unavailable — the dashboard renders without it. */
  coins?: { balance: number; lifetimeEarned: number } | null;
  hasAssessment: boolean;
  priceInr?: number;
  /**
   * How many days THIS learner's own plan runs to — 90 for a first-year, 110 for a
   * second-year, whatever the admin has set for their stage. The navigation named a
   * constant before this, which was wrong for every second-year and for any tenant that
   * had changed the Foundation length.
   */
  programDays?: number;
  /** The learner's career stage, for screens that name their year. */
  stage?: string | null;
  name?: string;
  firstName?: string;
  level?: LevelInfo;
  coderScore?: { score: number; parts: { label: string; earned: number; max: number }[] };
  percentileAhead?: number | null;
  /** Rank among every CareerPilot member, by XP. */
  globalRank?: { rank: number; participants: number } | null;
  /** The skill meter: Skill DNA by skill area (early = only low-confidence evidence in that area). */
  skills?: { key: string; label: string; score: number; early?: boolean }[];
  careerScore?: number | null;
  careerLevel?: string | null;
  pathwayLabel?: string;
  stats?: {
    solved: number; solvedToday: number; totalProblems: number;
    accuracy: { pct: number; attempts: number } | null;
    streak: number; longestStreak: number; xp: number;
    day: number; totalDays: number; completedDays: number;
    interviews: number; bestInterview: number | null; resumeScore: number | null;
    cohortRank: number | null; cohortSize: number;
  };
  weekly?: {
    submissions: number; solved: number; totalAttempts: number;
    accuracyPct: number | null; accuracyDelta: number | null;
    xpThisWeek: number; xpLastWeek: number; xpDelta: number;
  };
  recentActivity?: { label: string; icon: string; color: string; xp: number; ago: string }[];
  missions?: { key: string; title: string; detail: string; category: string; type: string; xp: number; link?: string; needsAnswer?: boolean; verify?: 'interview' | 'resume'; done: boolean; answer?: string; feedback?: string }[];
  allDone?: boolean;
  dailyGoal?: { earned: number; target: number; pct: number; met: boolean };
  streakWeek?: { date: string; letter: string; active: boolean; isToday: boolean }[];
  activity?: { date: string; xp: number; label: string }[];
  badges?: Badge[];
  journey?: { key: string; label: string; fromDay: number; toDay: number; done: boolean; current: boolean }[];
  leaderboard?: { rank: number; name: string; xp: number; me: boolean }[];
  contests?: { id: string; title: string; prize: string | null; startAt: string; slug: string | null }[];
  shareSlug?: string | null;
  passwordSet?: boolean;
  /** False until onboarding is finished, so the home screen sends them to setup, not the paper. */
  setupCompleted?: boolean;
  entitled?: Record<string, boolean>;
  /**
   * The parts of the product this member cannot open, and why they would want them.
   *
   * Computed and sent by the server rather than worked out here: whether a section is free or
   * paid is the tenant's setting, and a client that decided it for itself would be a lock
   * anybody could remove with dev tools. Empty for a member with everything.
   */
  locked?: LockedSection[];
  /** Menu items the institute's admin has switched off for CareerPilot students (Student Features page). */
  hidden?: string[];
  /** A running trial: limited access for a set time. Null for everyone else. */
  trial?: TrialStatus | null;
}

/** A trial as the member and the admin see it (server: trialService.trialStatus). */
export interface TrialStatus {
  planName: string; startedAt: string; endsAt: string; daysLeft: number;
  items: { key: string; label: string; icon: string; on: boolean; limit: number | null; used: number | null; unit: string | null }[];
}
export interface TrialItemSetting { on: boolean; limit: number | null }
export interface TrialProblem { id: string; number: number; title: string; difficulty: string }
export interface TrialPlan { id: string; name: string; description: string; days: number; items: Record<string, TrialItemSetting>; active: boolean; running: number; updatedAt?: string; practiceProblems: TrialProblem[] }
export interface TrialCatalogueItem { key: string; label: string; icon: string; unit: string | null; hint: string | null }

/** One day of the ninety. A unit of curriculum, not a calendar date. */
export interface SpineDay {
  day: number;
  band: string;
  dayUnitId: string;
  title: string;
  skillKey: string;
  journeyTopic: string;
  subtopics: string[];
  /** How much of the day's journey this student is served, from what they were measured on. */
  depth: 'FULL' | 'STANDARD' | 'REVIEW';
  estimatedMinutes: number;
}

export interface SpineBand {
  key: string; label: string; blurb: string; fromDay: number; toDay: number;
}

/**
 * One shape, not a union — deliberately, and for the same reason the selector uses one.
 *
 * A discriminated union would make every screen narrow before it could read either half, and
 * this project's tsconfig does not narrow boolean literals reliably. Optional fields say what is
 * present in which state, and `available` is still the thing to branch on.
 */
export interface SpineOutcome {
  available: boolean;

  /** Present when available. */
  totalDays?: number;
  days?: SpineDay[];
  bands?: SpineBand[];
  estimatedMinutes?: number;

  /** Present when not. */
  reason?: 'CURRICULUM_INCOMPLETE' | 'NO_CONTEXT';
  message?: string;
  /** Which bands fall short. Named, so the wait can be explained rather than just announced. */
  shortfalls?: { band: string; needed: number; available: number }[];
  authored?: number;
  total?: number;
}

export interface SpineCoverage {
  total: number; filled: number; percent: number; spineDays: number;
  rows: { band: string; label: string; needed: number; available: number; short: number; complete: boolean }[];
  bands: (SpineBand & { days: number; variance: string })[];
}

/** A part of the member experience that can be locked on its own. Mirrors memberAccessPolicy. */
export type MemberSection =
  | 'score' | 'roadmap' | 'missions' | 'progress'
  | 'practice' | 'interview' | 'resume' | 'companies' | 'news'
  | 'coding' | 'playground' | 'communication' | 'experiences' | 'questionBooks' | 'mentor'
  | 'thinkingLab' | 'industryConnect';

export interface LockedSection {
  section: MemberSection;
  /** The PassportConfig entitlement that would open it. */
  featureKey: string;
  title: string;
  blurb: string;
}

// ── Roadmap ──
export interface RoadmapDay { day: number; date?: string; categories: string[]; titles: string[]; xp: number; done?: boolean; isToday?: boolean; isPast?: boolean; }
export interface RoadmapWeek { week: number; theme: string; fromDay: number; toDay: number; focusLabels: string[]; goal: string; days: RoadmapDay[]; completedDays: number; }
export interface RoadmapPhase { key: string; label: string; blurb: string; fromDay: number; toDay: number; weeks: RoadmapWeek[]; }
export interface Roadmap {
  totalDays: number; pathway: string; pathwayLabel: string; pathwayDescription: string;
  currentDay: number; startDate?: string; endDate?: string; phases: RoadmapPhase[];
  totalXp: number; earnedXp: number; completedDays: number; locked?: boolean; previewDays?: number;
}
export interface RoadmapResponse {
  needsAssessment?: boolean; roadmap?: Roadmap; entitled?: boolean;
  /** False when even the 7-day preview is closed — the tenant set roadmap_preview to paid. */
  canPreview?: boolean;
  /** When membership lapses. Null for a member with no expiry recorded. */
  accessExpiresAt?: string | null;
  priceInr?: number; careerScore?: number; level?: string;
}

// ── Practice ──
export interface PracticeListItem {
  id: string; kind: 'coding' | 'sql' | 'mcq'; title: string; category: string;
  difficulty: string; xp: number; count: number;
  /** This member's own standing. `solved` merges the attempt row with the older list. */
  solved?: boolean; attempts?: number; testsPassed?: number | null; testsTotal?: number | null;
  /** How many members have solved it. Only present for admin-authored problems. */
  solvedCount?: number; attemptCount?: number; estimatedMinutes?: number;
}
export interface PracticeDetail {
  problem: PracticeProblem;
  solved: boolean;
  attempts: number;
  testsPassed: number | null;
  testsTotal: number | null;
  /** Their last editor contents, so reopening resumes rather than restarts. */
  savedCode: string;
  savedLanguage: string;
  /** Always sent — it teaches the problem rather than giving it away. */
  explainerVideo: string;
  explainerVideoKey: string;
  solutionUnlocked: boolean;
  attemptsToUnlock: number;
  /**
   * ABSENT until unlocked — the server omits these keys entirely rather than sending them
   * for the client to hide, so there is nothing to read out of the network tab.
   */
  solutionVideo?: string;
  solutionVideoKey?: string;
  referenceSolution?: string;
}

export interface PracticeListResponse { locked?: boolean; priceInr?: number; problems: PracticeListItem[]; solved: string[]; xp?: number; streak?: number; }
export interface SchemaTable { table: string; columns: { column: string; type: string }[] }
export interface PracticeProblem {
  id: string; kind: 'coding' | 'sql' | 'mcq'; title: string; subtitle?: string; category: string;
  difficulty: string; xp: number; estimatedMinutes?: number; prompt: string;
  learningGoals: string[]; tip?: string; hints: string[];
  languages?: string[]; starter?: Record<string, string>;
  schemaNote?: string; schema: SchemaTable[];
  sampleTests: { input: string; expected: string }[]; testCount: number;
  questions: { q: string; options: string[] }[];
}
export interface RunOutcome {
  results: { index: number; hidden: boolean; passed: boolean; input: string; expected: string; got: string; error?: string }[];
  passedCount: number; total: number; allPassed: boolean; compilationError?: string;
  executionMs?: number; memoryMb?: number;
}
export interface SubmitOutcome extends Partial<RunOutcome> {
  passed: boolean; xpAwarded: number; xp: number; streak: number; longestStreak: number; alreadySolved?: boolean;
  review?: { index: number; q: string; options: string[]; chosen: number; answer: number; correct: boolean; explain?: string }[];
  correct?: number; total?: number;
}

// ── Mock interview ──
export interface InterviewTurn { role: 'interviewer' | 'candidate'; text: string; at?: string; }
export interface InterviewSession {
  hasRecording?: boolean;
  /** Wall-clock cap in seconds (intro rounds), or null for an untimed sitting. */
  timeLimitSec?: number | null;
  recordingDurationSec?: number | null;
  id: string; role: string; areas: string[]; interviewerName: string;
  companySlug?: string | null; companyName?: string | null;
  /** Which plan round this was, and what it was called at the time. Null on untyped mocks. */
  planRoundKey?: string | null;
  planRoundLabel?: string | null;
  maxQuestions: number; askedCount: number; status: 'in_progress' | 'completed' | 'abandoned';
  transcript: InterviewTurn[];
  evaluation?: {
    overallScore: number; readinessLevel: string; summary: string;
    strengths: string[]; improvements: string[]; recommendedPracticeAreas: string[];
    areaScores: { title: string; percentage: number; feedback: string }[];
    questionFeedback?: { question: string; verdict: 'strong' | 'okay' | 'weak'; whatWorked: string; whatToFix: string; betterAnswer: string }[];
  } | null;
  xpAwarded: number; startedAt: string; completedAt?: string;
}

// ── Resume ──
export interface ResumeSections {
  contact: { name: string; title?: string; email: string; phone: string; linkedin?: string; github?: string; portfolio?: string; location?: string };
  summary: string;
  experience: { company: string; role: string; from: string; to: string; current: boolean; bullets: string[] }[];
  education: { degree: string; college: string; university?: string; year?: string; cgpa?: string }[];
  skills: { category: string; items: string[] }[];
  projects: { name: string; tech: string[]; description: string; link?: string }[];
  certifications: { name: string; issuer: string; year?: string }[];
}
export interface ResumeScore {
  total: number;
  breakdown: { contact: number; summary: number; experience: number; education: number; skills: number; projects: number; ats: number };
  suggestions: { section: string; issue: string; fix: string }[];
  atsWarnings: string[]; keywordsFound: string[]; keywordsMissing: string[];
}

// ── Admin content ──
export interface MissionPoolItem { title: string; detail: string; type: string; xp: number; link?: string; }
export interface MissionPool { category: string; items: MissionPoolItem[]; }
export interface PathwayScoreRule { category: string; min?: number | null; max?: number | null; }
/** Who a pathway serves. Within a list = OR, across fields = AND, empty = no constraint. */
export interface PathwayMatch {
  enabled: boolean;
  priority: number;
  goals: string[];
  stages: string[];
  backgrounds: string[];
  scores: PathwayScoreRule[];
  fallback: boolean;
}
export interface PassportPathway { key: string; label: string; description: string; focus: string[]; weekThemes: string[]; stage?: string; match?: PathwayMatch; }
export interface PassportContentDoc {
  _id?: string; tenantId?: string;
  pathways: PassportPathway[];
  missionPools: MissionPool[];
  journeyDays: number;
  /** Missions served per day, 1–6. Absent on tenants that never set one — read as 3. */
  missionsPerDay?: number;
  /** Off = pathway rules are a draft and the built-in sorting still decides. */
  pathwayRulesActive?: boolean;
}
export interface ContentPreview {
  sampleFromRealStudent: boolean;
  days: { day: number; missions: { key: string; title: string; detail: string; category: string; xp: number; link?: string }[] }[];
  weeks: { week: number; theme: string; goal: string; focusLabels: string[] }[];
  totalXp: number; totalDaysGenerated: number;
}

export interface TodayMissions {
  /** The day being shown, and the member's real current day, so the UI can offer a step back. */
  today?: number;
  isPast?: boolean;
  locked?: boolean; needsAssessment?: boolean; priceInr?: number; reason?: string;
  day?: number; streak?: number; longestStreak?: number; xp?: number; allDone?: boolean;
  missions?: { key: string; title: string; detail: string; category: string; type: string; xp: number; link?: string; needsAnswer?: boolean; verify?: 'interview' | 'resume'; done: boolean; answer?: string; feedback?: string }[];
}

/** The shareable CareerPilot card — computed fresh on every request (server: passportShareCardService). */
export interface PassportCard {
  name: string; photo: string | null;
  stage: string | null; track: string | null; role: string | null; careerGoal: string | null;
  memberSince: string | null;
  /** 0–100 role readiness from the assessment; null until measured. */
  careerScore: number | null; careerLevel: string | null;
  /** 0–1000 composite — the same coder score Home shows. */
  coderScore: number;
  level: { level: number; title: string; progressPct: number; xpToNextLevel: number; nextTitle: string };
  stats: {
    xp: number; streak: number; longestStreak: number; coins: number | null; solved: number;
    daysCompleted: number; totalDays: number | null; testsPassed: number; interviews: number;
  };
  topSkills: { key: string; name: string; score: number }[];
  updatedAt: string;
}

export interface AssessQuestion { id: string; category: string; text: string; options: string[]; dependsOn?: { questionId: string; minChosen: number }; }
export interface AssessQuestionFull { _id?: string; category: string; text: string; options: string[]; correctIndex: number; weight: number; selfReport?: boolean; stages?: string[]; goals?: string[]; background?: string; dependsOn?: { questionId: string; minChosen: number }; }
/** One person's trip through CareerPilot, collapsed to a row. */
export interface ActivitySession {
  visitorId: string;
  firstSeen: string; lastSeen: string; durationMs: number;
  events: number; pages: number; actions: number; failures: number;
  userId: string | null; personName: string | null; personEmail: string | null;
  device: { browser?: string; browserVersion?: string; os?: string; deviceType?: string;
            screen?: string; language?: string; timezone?: string } | null;
  ip: string | null; lastRoute: string | null; lastName: string | null;
}

export interface ActivityEvent {
  _id: string; kind: 'page' | 'action' | 'api' | 'error'; name: string;
  route?: string; method?: string; status?: number;
  outcome: 'success' | 'failure' | 'info'; errorMessage?: string;
  durationMs?: number; meta?: any; createdAt: string;
  device?: ActivitySession['device']; ip?: string; userAgent?: string; referrer?: string;
  personName?: string; personEmail?: string;
}

export interface ActivitySummary {
  events: number; visitors: number; identified: number; failures: number; bots: number;
  byDevice: { key: string; visitors: number }[];
  byBrowser: { key: string; visitors: number }[];
  topPages: { name: string; views: number }[];
  topFailures: { name: string; message: string; count: number }[];
}

/** One concept as the Learning Studio lists it. */
/* ── The mega curriculum, as an author edits it ────────────────────── */

export type LearningUnitType =
  'CONCEPT' | 'WORKED_EXAMPLE' | 'PRACTICE' | 'DEBUG' | 'PROJECT' | 'CHECKPOINT' | 'REVIEW';

export type LearningUnitCategory =
  'UNIVERSAL' | 'DIRECTION' | 'ACADEMIC' | 'EXPLORATION' | 'ENRICHMENT';

export interface CurriculumLearningUnit {
  _id?: string;
  tenantId?: string;
  stageKey: string;
  moduleCode: string;
  topicCode: string;
  /** Stable identity. Never changes — a student’s completed work is keyed on it. */
  unitCode: string;
  title: string;
  description: string;
  displayOrder: number;
  skillKeys: string[];
  /**
   * Authored suitability. ABSENT means derived from unitType, which is what almost every unit
   * does; a list is a claim that this unit serves states its type does not.
   */
  suitableStates?: string[];
  prerequisiteSkillKeys: string[];
  prerequisiteUnitCodes: string[];
  learningOutcomes: string[];
  category: LearningUnitCategory;
  applicableDirections: string[];
  audience: { languages: string[]; years: string[]; branches: string[] };
  defaultDepth: 'FOUNDATION' | 'GUIDED' | 'STANDARD' | 'REVISION' | 'CHALLENGE';
  estimatedMinutes: number;
  unitType: LearningUnitType;
  mandatory: boolean;
  /** A region of the ninety-day shape. NOT a day number — days belong to a student’s plan. */
  band?: string;
  status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
  /** Set by the list endpoint. Absent when a unit is fetched on its own. */
  coverage?: UnitCoverage;
}

export type CurriculumUnitReadiness = 'EMPTY' | 'PARTIAL' | 'TEACHABLE' | 'ASSESSABLE' | 'READY';

export interface UnitCoverage {
  readiness: CurriculumUnitReadiness;
  /** What is still missing, in words an author can act on. */
  missing: string[];
  /** Everything it has is its topic's — nothing was written for this unit. */
  inheritedOnly: boolean;
  /**
   * Counted from UNIT-SPECIFIC content only. These are the numbers readiness turns on;
   * `inheritedCount` is reported beside them and never added to them.
   */
  ownTeaching: number;
  ownPractice: number;
  ownAssessment: number;
  /**
   * Bound ASSIGNMENTS only — the submission axis, which a quiz can never satisfy.
   *
   * Reported apart from `ownAssessment` because a PROJECT with two quizzes and no assignment
   * has assessment and still cannot be READY, and one merged number would hide the reason.
   */
  ownSubmission: number;
  boundAssessments: number;
  inheritedCount: number;
  /** Attached to this unit but unpublished — it resolves for nothing. */
  unpublishedAttached: number;
  items: number;
  types: string[];
  hasTeaching: boolean;
  hasPractice: boolean;
  /** Whether the publish bar is met right now, so the button can say why not. */
  publishable: boolean;
  /** PUBLISHED **and** READY. The only state a student's plan may be built from. */
  composerReady: boolean;
}

export interface DirectionCoverageRow {
  key: string; name: string;
  /** Units carrying this direction, and how many of those are published. */
  authored: number; published: number;
  /** Days of this direction a beginner who chose it actually receives. */
  daysInPlan: number;
  composed: boolean;
}

export interface DirectionCoverageReport {
  stageKey: string;
  programDays: number;
  directions: DirectionCoverageRow[];
  /** Directions a student can choose that give them no days of their own. */
  empty: string[];
  identicalPairs: { a: string; b: string }[];
}

export interface MegaCurriculumTopicRow {
  moduleCode: string;
  moduleName: string;
  moduleOrder: number;
  topicCode: string;
  topicTitle: string;
  topicSkillKeys: string[];
  /** The topic is part of the mandatory Foundation backbone, so every unit of it is. Set on the stage curriculum. */
  topicBackbone?: boolean;
  units: CurriculumLearningUnit[];
  published: number;
  drafts: number;
}

export interface MegaCurriculumSummary {
  topics: number;
  topicsWithUnits: number;
  totalUnits: number;
  published: number;
  drafts: number;
  /** Published AND ready. The gap between this and `published` is the real backlog. */
  composerReady: number;
  orphaned: number;
  /** How many units sit at each readiness rung. PARTIAL is the real backlog. */
  readiness?: Record<string, number>;
}

export interface BundleItem {
  _id: string;
  type: string;
  title: string;
  estimatedDuration: number;
  learningDepth: string | null;
  role: 'TEACH' | 'REINFORCE' | 'PRACTISE' | 'OTHER';
  /** Attached to this unit, rather than inherited from its topic or skills. */
  attached: boolean;
  /** Unpublished content resolves for nothing, however firmly it is attached. */
  isPublished: boolean;
}

export interface UnitBundle {
  /** Which hook resolved this content: the narrowest that matched. */
  via: 'unitCode' | 'topicCode' | 'skillKeys' | 'none';
  /** In teaching order — watch, read, see it done, practise, prove. */
  items: BundleItem[];
  hasTeaching: boolean;
  hasPractice: boolean;
  types: string[];
  resolvedMinutes: number;
}

export interface MemberActivityRow {
  _id: string; kind: 'page' | 'action' | 'api' | 'error'; name: string; route?: string; method?: string; status?: number;
  outcome?: string; errorMessage?: string; durationMs?: number; meta?: any; ip?: string; referrer?: string;
  sessionId?: string; visitorId: string; userId?: string; createdAt: string;
  device?: { browser?: string; os?: string; deviceType?: string; screen?: string };
}
export interface MemberActivity {
  summary: { events: number; firstSeen: string; lastSeen: string; sessions: number; pages: number; actions: number; failures: number; browsers: number } | null;
  devices: { deviceType: string | null; browser: string | null; os: string | null; events: number; lastSeen: string }[];
  rows: MemberActivityRow[];
  nextBefore: string | null;
}

export interface VideoScriptScene { heading: string; visual: string; narration: string; notes: string }
export type VideoScriptStatus = 'DRAFT' | 'READY' | 'SHOT';
export interface VideoScriptInput {
  title: string; objective: string; targetMinutes: number; presenter: string;
  scenes: VideoScriptScene[]; status: VideoScriptStatus;
}
export interface VideoScript extends VideoScriptInput {
  _id: string; unitCode: string; updatedAt: string; updatedBy?: string; createdBy?: string;
}

export interface XpRewardsOverview {
  xpThisMonth: { total: number; members: number } | null;
  conversion: { enabled: boolean; xpPerCoin: number; coinsThisMonth: number; members: number } | null;
  waiting: number | null;
  liveRewards: number | null;
  store: { open: boolean; budgetRupees: number; leftRupees: number } | null;
}

export interface TaskXpType { key: string; label: string; defaultXp: number; xp: number }

export interface TaskPrices {
  byType: Record<string, number>;
  /** 'content:<id>' | 'module:<id>' → XP. */
  overrides: Record<string, number>;
  dayBonus: number;
  tasksEnabled: boolean;
  max: number;
}

export interface XpRuleRow {
  eventKey: string; enabled: boolean; xp: number; dailyLimit: number; streakQualifying: boolean;
}

/** A badge as the admin defines it: its look, and the rule that earns it. */
export interface AdminBadgeRow {
  key: string; name: string; description: string; iconKey: string; color: string; active: boolean;
  conditionType: string; threshold: number; eventKey: string | null; displayOrder: number;
  /** The rule in words — what members see when the description is empty. */
  rule: string;
  /** How many members hold it. */
  earnedBy: number;
}
export interface BadgeCondition { type: string; label: string; unit: string; max?: number; needsEvent?: boolean }
export interface BadgeInput { name: string; description: string; iconKey: string; color: string; conditionType: string; threshold: number; eventKey?: string | null; active?: boolean }
/** What each level is called, level 1 first, and the XP that reaches each. */
export interface LevelLadder { names: string[]; defaults: string[]; custom: boolean; max: number; nameMax: number; xpToReach: number[] }

export interface AdminGamification {
  rules: XpRuleRow[];
  badges: AdminBadgeRow[];
  badgeConditions: BadgeCondition[];
  levels: LevelLadder;
  settings: any;
  events: { key: string; name: string; description: string; defaultXp: number }[];
  taskTypes: TaskXpType[];
  taskOverrides: { refKind: 'content' | 'module'; refId: string; xp: number; title: string; updatedAt: string }[];
  taskXpMax: number;
}

export interface UnitStudentPreview {
  preview: true;
  unit: { unitCode: string; title: string; description: string; learningOutcomes: string[]; unitType: string; status: string };
  /** DayPlan-shaped items; content items carry `content`, quiz/assignment items carry `live` and `editPath`. */
  items: any[];
  notShown: { _id: string; title: string; type: string }[];
}

export interface UnitContent {
  unitCode: string;
  title: string;
  /** Rows explicitly bound to this unit. */
  attached: BundleItem[];
  /** Unclaimed rows that already serve this unit's topic or skills. */
  candidates: BundleItem[];
  /** What the unit teaches from right now, attached or inherited. */
  resolved: UnitBundle;
  /** Attached rows that teach nothing because they were never published. */
  attachedButUnpublished: string[];
  teachingOrder: string[];
}

export interface MegaCurriculumOptions {
  stageKey: string;
  modules: { moduleCode: string; moduleName: string; displayOrder: number }[];
  topics: {
    moduleCode: string; topicCode: string; title: string; skillKeys: string[];
    defaultDepth: string | null; applicableDirections: string[];
  }[];
  /** Active SKILL nodes only — the same set the backend will accept on save. */
  skills: { key: string; name: string }[];
  unitTypes: LearningUnitType[];
  categories: LearningUnitCategory[];
  bands: { key: string; label: string; days: number }[];
  /**
   * Served by the server, never restated here.
   *
   * Both of these were hardcoded in the screen, which meant the client held a second copy of a
   * server taxonomy and the two could drift with nothing to report it. The seven authorable
   * states are a deliberate SUBSET of the nine AssignmentStates — see the server policy for why
   * LOCKED and NOT_RELEVANT are excluded — so deriving them in the client was never possible
   * anyway without repeating the reasoning.
   */
  suitableStates: string[];
  directions: { key: string; name: string }[];
}

export interface UnitAssessment {
  _id: string;
  kind: 'QUIZ' | 'ASSIGNMENT';
  title: string;
  /** Bound but not live teaches nobody — the same trap as attached-but-unpublished content. */
  live: boolean;
  /** Only an ASSIGNMENT can receive submitted work. */
  countsAsSubmission: boolean;
  type?: string;
  totalMarks?: number;
}

export interface UnitAssessments {
  unitCode: string;
  unitType: string;
  bound: UnitAssessment[];
  /** Assessments carrying no unit code at all. One already bound elsewhere is never offered. */
  candidates: UnitAssessment[];
  hasAssessment: boolean;
  hasSubmission: boolean;
}

/* ── The student's Foundation journey ──────────────────────────────────────
   Deliberately narrow. No composer scores, no role allocations, no readiness
   rungs, no master-curriculum inventory — those are authoring instruments and
   tell a student nothing about what to do today. */

export type JourneyDayStatus = 'COMPLETED' | 'CURRENT' | 'SKIPPED' | 'UPCOMING';

export interface FoundationJourneyDaySummary {
  day: number;
  title: string;
  activities: number;
  minutes: number;
  status: JourneyDayStatus;
  /**
   * Roadmap metadata — overview only, never content. The topic and module a day belongs to, what kind of
   * day it is, and its objective in the unit's own words. Null when the curriculum cannot name them.
   */
  topic?: string | null;
  module?: string | null;
  kind?: 'LESSON' | 'PRACTICE' | 'DEBUGGING' | 'PROJECT' | 'CHECKPOINT' | null;
  objective?: string | null;
  /** The server's ladder: true while the day before is unfinished. The day endpoint refuses a locked day. */
  locked?: boolean;
  /** Which gate is shut: work still to do, a day the calendar has not reached, or (Roadmap V2) the foundation checks. */
  lockedReason?: 'DAY_LOCKED' | 'NOT_TODAY_YET' | 'FOUNDATION_GATE';
  /** Roadmap V2 only: which part of the plan the day belongs to. */
  phase?: JourneyPhase;
}

/** Roadmap V2: revision of what a member studied before, the bridge of earlier years, then the year. */
export type JourneyPhase = 'REVISION' | 'BRIDGE' | 'YEAR';
export interface RoadmapPhaseSummary { phase: JourneyPhase; days: number; completed: number; fromDay: number | null; toDay: number | null }
export interface RoadmapGateSummary { open: boolean; passed: number; total: number; firstYearDay: number | null; pending: number; flagged: number }

/**
 * One interface rather than a discriminated union on `available`.
 *
 * The union was the natural shape and does not survive this project's compiler settings:
 * with `strictNullChecks: false`, narrowing a `T | null` state through a discriminant leaves
 * the union undiscriminated in the guarded branch, so every consumer would need a cast. A
 * single interface with the unavailable-state fields optional says the same thing and reads
 * correctly at every call site.
 */
/** One welcome day as the roadmap shows it. `day` is a label ("0.3"), never a number to compute with. */
export interface OrientationRoadmapDay {
  day: string;
  /** The orientation day this opens, which is how the orientation screen addresses it. */
  dayNumber: number;
  title: string;
  blurb: string;
  minutes: number;
  status: 'COMPLETED' | 'CURRENT' | 'UPCOMING';
  locked: boolean;
  /** Work still to do, or simply not today yet. Two different things to tell somebody. */
  lockedReason?: 'PREVIOUS_DAY' | 'NOT_TODAY_YET';
  /** The IST midnight that opens it, when the calendar is what is holding it. */
  opensAt?: string | null;
}

export interface OrientationRoadmap {
  mandatory: boolean;
  /** True when this member gets one day per calendar day as well as one after another. */
  paced?: boolean;
  complete: boolean;
  /** True while the welcome must be finished before Day 1 opens. */
  blocking: boolean;
  totalDays: number;
  completedDays: number;
  days: OrientationRoadmapDay[];
}

export interface FoundationJourney {
  available: boolean;
  /** The programme length, available or not. A student is told how long it will be before it exists. */
  totalDays: number;
  /**
   * Which stage this journey teaches — `foundation` for a first-year, `build` for a second-year.
   *
   * The key is for branching, the label for showing. Both are optional because a server that has
   * not been deployed yet sends neither, and a screen that falls back to its old wording is a
   * better failure than one that renders "undefined Journey".
   */
  stage?: string | null;
  stageLabel?: string | null;
  /**
   * The five welcome days, labelled 0.1–0.5 and shown BEFORE Day 1.
   *
   * They are not part of `totalDays` and never will be: ninety days means ninety learning days.
   * Null when the tenant has the welcome switched off, and the roadmap then renders as before.
   */
  orientation?: OrientationRoadmap | null;
  /** The engine that plans this student. UNIT means this journey is their one roadmap. */
  engine?: 'UNIT' | 'TOPIC';
  /** The enrolment whose day player works a day through. Null until the journey exists. */
  enrollmentId?: string | null;
  /** FULL for a member; PREVIEW shows only the first `previewDays` days; LOCKED shows nothing. */
  access?: 'FULL' | 'PREVIEW' | 'LOCKED';
  previewDays?: number;
  /** How many of the ninety are locked in a preview. */
  lockedDays?: number;
  /** The preview days in full — topics and what each contains, nothing to open. */
  preview?: FoundationPreviewDay[];

  /** Present only when `available` is false. */
  reason?: string;
  message?: string;

  /** Present only when `available` is true. */
  /** Already named for the stage by the server: "CareerPilot Build Journey". */
  title?: string;
  currentDay?: number;
  completedCount?: number;
  percentComplete?: number;
  startedAt?: string | null;
  days?: FoundationJourneyDaySummary[];
  /** Roadmap V2 only: the plan's phases with their progress, and the foundation gate. */
  roadmapVersion?: 'ROADMAP_V2';
  phases?: RoadmapPhaseSummary[];
  gate?: RoadmapGateSummary | null;
}

export interface FoundationJourneyActivity {
  id: string;
  kind: 'content' | 'quiz' | 'assignment' | 'codeSnippet' | 'mockInterview';
  title: string;
  type: string;
  required: boolean;
  /** Holds the day open until it is done. */
  gating: boolean;
  minutes: number;
  order: number;
  contentId: string | null;
  sourceId: string | null;
  /** Finished by this student (a lesson marked done; a checkpoint, project or code task submitted). */
  done?: boolean;
  /** CareerPilot XP this task pays when finished — once. 0 for tasks paid by their own event. */
  xp?: number;
}

export interface FoundationJourneyDay {
  day: number;
  totalDays: number;
  title: string;
  /** The unit's own description. The one piece of planning a student is shown. */
  objective: string | null;
  outcomes: string[];
  status: JourneyDayStatus;
  minutes: number;
  activities: FoundationJourneyActivity[];
  /** Extra XP for finishing every required task of the day. */
  dayBonusXp?: number;
  /** XP this read settled — work finished elsewhere since the last read. The page refreshes XP and the goal on it. */
  xpJustPaid?: number;
}

/** One day of a non-member's preview: what it teaches, and what it contains. Nothing to open. */
export interface FoundationPreviewDay {
  day: number;
  title: string;
  objective: string | null;
  outcomes: string[];
  minutes: number;
  activities: { title: string; type: string; minutes: number; gating: boolean }[];
}

/** Admin view of one member's journey. Unlike the member's own, it names the unit behind each day. */
export interface AdminFoundationJourneyDay {
  day: number;
  /** Roadmap V2 only. */
  phase?: JourneyPhase;
  title: string;
  unitCode: string | null;
  unitType: string | null;
  /** PUBLISHED, DRAFT, ARCHIVED — or MISSING when the unit no longer exists. */
  unitStatus: string;
  activities: number;
  checkpoint: boolean;
  project: boolean;
  minutes: number;
  status: JourneyDayStatus;
}

export interface AdminFoundationJourney {
  available: boolean;
  engine: 'UNIT' | 'TOPIC';
  student: { name: string; email: string | null; stage: string | null; careerTrack?: string | null };
  totalDays: number;
  message?: string;
  curriculumId?: string;
  enrollmentId?: string | null;
  currentDay?: number;
  completedCount?: number;
  percentComplete?: number;
  startedAt?: string | null;
  days?: AdminFoundationJourneyDay[];
  /** NO_JOURNEY, or NOT_CONFIGURED when this tenant cannot serve the Foundation journey. */
  reason?: string;
  /** Whether this member sees all ninety days or only the preview. */
  access?: { level: 'FULL' | 'PREVIEW' | 'LOCKED'; previewDays: number } | null;
  /** Roadmap V2 only: how the days split, what was given up to fit them, and the foundation gate. */
  roadmapVersion?: 'ROADMAP_V2';
  phaseDays?: { revision?: number; bridge: number; year: number } | null;
  v2Report?: { dropped: Record<string, number>; overflowDays: number; programDays: number } | null;
  /** Admin rebuilds of this roadmap, newest last. */
  rebuilds?: { at: string; by: string | null; from: string; mode: 'FULL' | 'FUTURE'; keptDays: number }[];
  gate?: {
    open: boolean; passed: number; total: number; firstYearDay: number | null;
    pending: { quizId: string; title: string; dayNumber: number; attempts: number }[];
    flagged: { quizId: string; title: string; dayNumber: number; attempts: number }[];
  } | null;
}

/** Whether a tenant can serve the Foundation journey. Decided by provisioning, not by a switch. */
export interface FoundationReadiness {
  configured: boolean;
  reason: 'NO_PRODUCTION_CURRICULUM' | 'NO_SKILL_CHECK' | null;
  publishedUnits: number;
  skillCheckMappings: number;
  message: string | null;
}

export interface StudioConcept {
  skillKey: string; skillName: string; category: string; difficulty: string;
  unitId: string | null; unitTitle: string; unitStatus: string | null;
  version: number; stepCount: number; estimatedMinutes: number; resources: number;
  readiness: number; blocking: string[];
  status: 'NOT_CONFIGURED' | 'INCOMPLETE' | 'READY' | 'PUBLISHED' | 'ARCHIVED';
}

export interface LearningStep {
  stepId: string; sequence: number; phase: string;
  /** The part of the skill this belongs to — "Loops" divides into "For loops". Grouping only. */
  topic?: string;
  /**
   * The idea inside the topic — "Counting with range". Grouping only.
   *
   * The second level exists because "For loops" is itself three or four steps: the
   * explanation, the worked example, the practice. With one level an author looking at
   * fourteen steps under Loops cannot see where for loops end and while loops begin.
   */
  subtopic?: string;
  resourceId?: string; titleOverride?: string;
  estimatedMinutes: number; required: boolean;
  scoreWindow?: { min: number | null; max: number | null };
  audience?: MaterialAudience; notes?: string;
}

export interface LearningUnit {
  _id?: string; skillKey: string; title: string; description?: string;
  learningOutcomes: string[]; steps: LearningStep[];
  estimatedMinutes: number; version: number; status: string;
  audience?: MaterialAudience; completionThreshold?: number;
}

export interface UnitReadinessCheck { key: string; label: string; passed: boolean; required: boolean; detail: string; }
export interface UnitReadiness { publishable: boolean; percent: number; checks: UnitReadinessCheck[]; blocking: string[]; }

export interface StudioResource {
  id: string; title: string; resourceType: string; workTypes: string[]; priority: number; hasContent: boolean;
}

export interface AssessmentBank { _id?: string; tenantId: string; title: string; maxQuestions?: number; questions: AssessQuestionFull[]; categories?: AssessCategory[]; }
/** A scoring category. `weight` scales its contribution to the career score (0.1–3). */
export interface AssessCategory { key: string; label: string; weight: number; order?: number; }
/** What a category is carrying — the delete guard reads from this. */
export interface CategoryUsage { key: string; questions: number; missions: number; pathways: number; }
export interface CategoryScore { key: string; label: string; score: number; }
export interface AssessResult {
  careerScore: number; level: string; levelKey: string;
  categoryScores: CategoryScore[]; strengths: string[]; weaknesses: string[];
  pathway: string; pathwayLabel: string;
  weekPreview: { day: number; title: string; detail: string }[];
  takenAt?: string;
}

// ── Public funnel (no auth) ──
const PUB = (process.env.REACT_APP_API_URL || '/api/v1') + '/public/passport';
export const passportPublicApi = {
  getConfig: async (tenant: string) => {
    const { data } = await axios.get(`${PUB}/config`, { params: { tenant } });
    return data as { success: boolean; enabled: boolean; onboardingFields: OnboardingField[]; priceInr: number; tenantId: string; registration?: RegistrationWindow };
  },
  signup: async (body: { tenant: string; name: string; mobile: string; email: string; fields: Record<string, any> }) => {
    /**
     * Attribution is attached here rather than asked of every caller.
     *
     * A signup form has no business knowing about campaigns, and a second call site that forgot
     * to pass it would lose the provenance of every lead it created without anything failing.
     */
    const { data } = await axios.post(`${PUB}/signup`, { ...body, attribution: attributionForSubmit() });
    return data as { success: boolean; token: string; otp: { sent: boolean; channel: string; devCode?: string; throttledSeconds?: number } };
  },
  verify: async (token: string, code: string) => {
    const { data } = await axios.post(`${PUB}/verify`, { token, code });
    // `onboardingCompleted` decides where the member lands — see Join.tsx.
    return data as { success: boolean; token: string; tenantId: string; user: any; onboardingCompleted?: boolean };
  },
  resend: async (token: string) => {
    const { data } = await axios.post(`${PUB}/resend`, { token });
    return data as { success: boolean; otp: any };
  },
  // Returning-member login (password — free) and OTP-login start (then reuse verify()).
  loginPassword: async (tenant: string, identifier: string, password: string) => {
    const { data } = await axios.post(`${PUB}/login-password`, { tenant, identifier, password });
    return data as { success: boolean; token: string; tenantId: string; user: any; onboardingCompleted?: boolean };
  },
  loginOtp: async (tenant: string, mobile: string) => {
    const { data } = await axios.post(`${PUB}/login-otp`, { tenant, mobile });
    return data as { success: boolean; token: string; otp: { sent: boolean; channel: string; devCode?: string; throttledSeconds?: number } };
  },
  getCard: async (slug: string) => {
    const { data } = await axios.get(`${PUB}/card/${encodeURIComponent(slug)}`);
    return data as { success: boolean; card: PassportCard };
  },
};

export default passportApi;

export interface LeaderboardRow { rank: number; name: string; city: string; xp: number; streak: number; me: boolean; }
export interface LeaderboardResponse {
  total: number; rows: LeaderboardRow[]; me: LeaderboardRow | null; percentile: number | null;
}

export interface MemberProfile {
  name: string; email: string; mobile: string;
  degree: string; branch: string; yearOfStudy: string; careerGoal: string; city: string;
}

// ── Coins ──
export interface CoinConfig {
  enabled: boolean; coinsPerRupee: number; monthlyEarnCap: number;
  annualRealCostBudgetInr: number; expiryMonths: number; minRedemption: number;
  referrerCoins: number; refereeCoins: number; referralMonthlyCap: number;
  freeMembersAccrue: boolean;
  xpConversionEnabled: boolean; xpPerCoin: number; xpConversionStartsAt: string | null;
}
/** XP → coins: the rate, and what a member can convert now. */
export interface XpConversionStatus {
  enabled: boolean; xpPerCoin: number; startsAt: string | null;
  earnedXp: number; convertedXp: number; availableXp: number;
  coinsAvailable: number; monthlyRoom: number | null; xpToNextCoin: number;
}
export interface CoinRule {
  eventKey: string; label: string; coins: number;
  dailyCap: number; monthlyCap: number; enabled: boolean;
}
export interface CoinHistoryRow { at: string; coins: number; eventKey: string; note: string; balanceAfter: number; }
export interface CoinsResponse {
  enabled: boolean; balance: number; lifetimeEarned: number; lifetimeSpent: number;
  redeemable: boolean; minRedemption: number; monthlyEarnCap: number; expiryMonths: number;
  earnRules: { eventKey: string; label: string; coins: number; dailyCap: number }[];
  conversion?: XpConversionStatus;
  history: CoinHistoryRow[];
}
export interface CoinLedgerRow { at: string; coins: number; eventKey: string; note: string; balanceAfter: number; member: string; }
export interface CoinAdminResponse {
  config: CoinConfig;
  rules: CoinRule[];
  knownEvents: { key: string; label: string }[];
  stats: {
    totalIssued: number; awards: number; earningMembers: number;
    worstCaseInrPerMember: number; membershipPriceInr: number; budgetInrPerMember: number;
  };
}

// ── Tech News ──
export interface NewsItem {
  id: string; title: string; summary: string; note?: string;
  url: string; source: string; imageUrl?: string; tags: string[]; publishedAt: string;
}
export interface AdminNewsItem extends NewsItem { status: 'draft' | 'published'; aiGenerated?: boolean; }
export interface NewsDraft {
  title: string; summary: string; source: string; imageUrl: string; tags: string[]; url: string;
}

// ── Company Questions ──
export interface TaxItem { key: string; label: string; order: number; enabled: boolean; count?: number; }
export interface CompanyRow { id: string; name: string; slug: string; type: string; logoUrl: string; about: string; questionCount: number; }
/** Who founded a company, for the student-facing profile. */
export interface CompanyFounder { name: string; title?: string }

// ── Company preparation (Module 15) ──

export type FitStatus =
  | 'NOT_ASSESSED' | 'LIMITED_EVIDENCE' | 'PRIORITY_GAP' | 'NEEDS_WORK' | 'ON_TRACK' | 'STRONG';

export interface CompanySkillFitRow {
  skillKey: string; skillName: string; importance: string; weight: number;
  targetLevel: string; targetScore: number;
  /** Null means never measured. NOT zero — the UI must never render it as a score. */
  studentScore: number | null;
  skillConfidence: string | null;
  evidenceCount: number;
  gapPoints: number | null;
  status: FitStatus;
  countedInFit: boolean;
  skillInactive: boolean;
}

export interface CompanyFitView {
  available: boolean;
  reason?: string;
  message?: string;
  /** Null when nothing is sufficiently assessed. Render "not assessed", never 0%. */
  readiness?: number | null;
  coverage?: number;
  confidence?: 'LOW' | 'MEDIUM' | 'HIGH';
  classification?: string | null;
  classificationLabel?: string | null;
  profileVersion?: number;
  role?: { key: string; matched: boolean };
  summary?: {
    requiredSkills: number; assessedSkills: number; priorityGaps: number; needsWork: number;
    onTrack: number; strong: number; limitedEvidence: number; notAssessed: number;
  };
  skills?: CompanySkillFitRow[];
  strengths?: CompanySkillFitRow[];
  gaps?: CompanySkillFitRow[];
  unknowns?: CompanySkillFitRow[];
}

export interface EligibilityCriterionRow {
  key: string; label: string; required: string;
  studentValue: string | null;
  status: 'MET' | 'NOT_MET' | 'UNKNOWN';
  detail: string;
}

export interface CompanyEligibilityView {
  verdict: 'ELIGIBLE' | 'POTENTIALLY_ELIGIBLE' | 'NOT_ELIGIBLE' | 'UNKNOWN';
  decidedBy: string | null;
  criteria: EligibilityCriterionRow[];
  verified: boolean;
  message: string;
}

export interface CompanyReadinessView {
  company: { slug: string; name: string; type: string; logoUrl: string };
  stage: string | null;
  fit: CompanyFitView;
  eligibility: CompanyEligibilityView;
  roleReadiness: {
    available: boolean; reason?: string; message?: string;
    role?: { key: string; name: string };
    readiness?: number | null; coverage?: number; confidence?: string;
  };
}

export interface CompanyPreparationView {
  company: { slug: string; name: string };
  available: boolean;
  reason?: string;
  message?: string;
  stage?: string | null;
  /** LONG_TERM for a member years from placement; ACTIVE for one preparing now. */
  horizon?: 'LONG_TERM' | 'ACTIVE';
  profileVersion?: number;
  focus: { skillKey: string; skillName: string; current: number | null; target: number; gap: number | null; importance: string; status: string }[];
  validate: { skillKey: string; skillName: string; importance: string; status: string }[];
  strengths?: { skillKey: string; skillName: string; current: number | null }[];
  roundSkills?: { roundKey: string; skillKeys: string[] }[];
  notes?: string;
}

export interface CompanyOverviewRow {
  slug: string; name: string; type: string; logoUrl: string; questionCount: number;
  readiness: number | null;
  classification: string | null;
  classificationLabel: string | null;
  gaps: number | null;
  isTarget: boolean;
  isPrimaryTarget: boolean;
}


// ── CareerPilot analytics (Module 16) ──

/** Whether a figure could be produced at all. `unavailable` is never rendered as 0. */
export type AnalyticsCoverage = 'available' | 'partial' | 'unavailable';

export interface CoverageNote { coverage: AnalyticsCoverage; reason?: string }

export interface AnalyticsEnvelope {
  policyVersion: string;
  range: { from: string; to: string; days: number; timezone: string };
  generatedAt: string;
  data: any;
  coverage: Record<string, AnalyticsCoverage | CoverageNote>;
}

export interface HealthFindingView {
  area: string;
  severity: 'ERROR' | 'WARNING' | 'INFO';
  code: string;
  message: string;
  action: string;
  meta?: Record<string, any>;
}

export interface ConfigHealthView {
  checkedAt: string;
  findings: HealthFindingView[];
  counts: { error: number; warning: number; info: number };
}

export interface LaunchReadinessView {
  status: 'NOT_READY' | 'READY_WITH_WARNINGS' | 'READY';
  checkedAt: string;
  summary: { error: number; warning: number; info: number };
  areas: {
    area: string; label: string; status: 'PASS' | 'WARNING' | 'FAIL';
    errors: number; warnings: number; findings: HealthFindingView[];
  }[];
  disclaimer: string;
}

export interface CompanyProfileRow {
  id: string; companySlug: string; roleKey: string; version: number;
  status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
  skillRequirements: { skillKey: string; importance: string; targetLevel: string; weight: number }[];
  roundSkills: { roundKey: string; skillKeys: string[] }[];
  careerStages: string[];
  sources: { type: string; reference: string; note?: string; verifiedAt?: string | null }[];
  preparationNotes: string;
  effectiveFrom: string | null;
  lastReviewedAt: string | null;
  publishedAt: string | null;
  daysSinceReview: number | null;
  reviewDue: boolean;
}

export interface CompanyProfileAdmin {
  company: { slug: string; name: string };
  profiles: CompanyProfileRow[];
  roles: { key: string; name: string }[];
  /** The canonical catalogue. The editor picks from this — it never accepts a typed key. */
  skills: { key: string; name: string }[];
  rounds: { key: string; label: string }[];
  importanceOptions: string[];
  targetLevelOptions: string[];
  defaultWeights: Record<string, number>;
}

export interface CompanyOverviewView {
  locked?: boolean;
  priceInr?: number;
  role?: { key: string } | null;
  stage?: string | null;
  maxTargets?: number;
  companies?: CompanyOverviewRow[];
}
export interface CompanyQuestionRow {
  id: string; role: string; round: string; category: string; difficulty: string;
  year: number | null; questionText: string; answer: string; tags: string[];
  aiPredicted: boolean; source: string; practiceProblemId: string; upvotes: number;
  /** How many separate reports of this question exist. 1 = a single admin entry. */
  askedCount?: number;
  lastAsked?: string | null;
}
/** A figure and the sample behind it. `value` is null when nothing supports it yet. */
export interface Stat<T = number> { value: T | null; n: number }

export interface SalaryBand { role: string; minLpa: number; maxLpa: number; note?: string; indicative: boolean }

export interface CompanyStats {
  avgRounds: Stat; avgDurationDays: Stat; offerRate: Stat; rating: Stat;
  difficultyFelt: Stat<string>; experiences: number;
  rounds: { key: string; questions: number; attemptedPct: number | null }[];
  totals: { questions: number; askedThisYear: number; highFrequency: number; avgSuccessRate: Stat };
}

export interface MockQuestion {
  id: string; text: string; options: string[]; category: string; difficulty: string;
  /** Written by AI rather than taken from the bank — surfaced to the student. */
  generated: boolean;
}
export interface MockAttempt {
  id: string; companySlug: string; companyName: string;
  startedAt: string; endsAt: string; status: 'in_progress' | 'submitted' | 'expired';
  totalQuestions: number; passingPct: number;
  sections: { name: string; category: string; durationMins: number; questions: MockQuestion[] }[];
  answers: { questionId: string; chosen: number }[];
}
export interface MockResult {
  score: number; correct: number; total: number; passed: boolean; passingPct: number;
  generatedCount: number; bankedCount: number;
  review: {
    id: string; text: string; options: string[]; chosen: number | null;
    correctIndex: number; right: boolean; explanation: string; generated: boolean;
  }[];
}

export interface ReadinessCheck { key: string; label: string; done: boolean; detail: string; required: boolean }
export interface ReadinessRow {
  id: string; name: string; slug: string; type: string;
  ready: boolean; score: number; missing: string[]; checks: ReadinessCheck[];
  aiDrafted: Record<string, boolean>; verified: Record<string, boolean>;
}
export interface PatternRound {
  key: string; order: number; name: string; durationMins?: number;
  tests: string[]; description?: string; cutoff?: string; tip?: string;
}

export interface AdminExperience {
  id: string; companySlug: string; role: string; interviewedOn: string;
  roundsFaced: string[]; durationDays: number | null; outcome: string;
  difficultyFelt: string; rating: number | null; review: string; status: string; student: string;
}

export interface CompanyDetail {
  company: {
    id: string; name: string; slug: string; type: string; logoUrl: string; about: string;
    roles: string[]; location: string; industry: string; employeeBand: string; website: string;
    founders?: CompanyFounder[]; foundedYear?: number | null; revenue?: string;
    tips: string[]; salaryBands: SalaryBand[];
    /** Null until an admin has verified it — never shown to a student unverified. */
    eligibility: {
      cgpaMin?: number; tenthMin?: number; twelfthMin?: number;
      backlogsAllowed?: number; branches: string[]; notes?: string;
    } | null;
    hiringTimeline: string;
  };
  pattern: { role: string; totalDurationDays: number | null; rounds: PatternRound[] } | null;
  stats: CompanyStats;
  rounds: TaxItem[]; categories: TaxItem[]; difficulties: TaxItem[];
  questions: CompanyQuestionRow[];
}
export interface AdminQuestion extends CompanyQuestionRow { status: string; companySlug: string; reviewNote: string; contributor: string; }
export interface CompanyAdmin {
  taxonomy: { rounds: TaxItem[]; categories: TaxItem[]; difficulties: TaxItem[]; companyTypes: TaxItem[] };
  companies: (CompanyRow & { active: boolean })[];
  pendingCount: number;
}

/** One step of the drop-off funnel, with what to do about the people in it. */
export interface FunnelStage {
  key: string; label: string; meaning: string; action: string;
  /** 1 is the warmest lead — closest to paying. */
  heat: number;
  count: number;
}
export interface FunnelTotals { members: number; paid: number; revenueInr: number; unverifiedShare: number; }
export interface FunnelMember {
  id: string; name: string; email: string; phone: string; stage: string;
  /** Days since they last did anything. How cold the lead is. */
  stuckDays: number;
  joinedAt: string; lastTouch: string;
  careerScore?: number | null; pathway?: string | null; pendingAmountInr?: number;
}

/** One authored task within a curriculum day. */
export interface CurriculumItem {
  title: string; detail: string; type: string; xp: number; link?: string; category?: string;
}
export interface CurriculumDay { day: number; theme?: string; items: CurriculumItem[]; }
export interface CurriculumDoc {
  pathwayKey: string; days: CurriculumDay[]; aiDraftedAt?: string | null; journeyDays: number;
}
export interface CurriculumTrack {
  key: string; label: string; variants: string[]; days: number; journeyDays: number;
}

/**
 * The normalized answer to "who is this student right now?".
 *
 * `derived` is resolved by the server on every read. Nothing in the client recomputes
 * stage, background or months remaining — a second implementation in React is how the
 * two would come to disagree.
 */
export interface CareerContext {
  tenantId: string;
  studentId: string;
  education: {
    program: string | null; degree: string | null; branch: string | null;
    currentAcademicYear: string | null;
    graduationYear: number | null; graduationMonth: number | null;
    collegeName: string | null; university: string | null;
  };
  location: { country: string | null; state: string | null; city: string | null };
  career: {
    domain: string;
    primaryRole: string;
    secondaryRole: string | null;
    careerGoal: string | null;
    /** What they want to work in. */
    preferredProgrammingLanguages: string[];
    preferredTechnologies: string[];
    /** What they already know, from StudentProfile. Read-only. */
    knownProgrammingLanguages: string[];
  };
  availability: { minutesPerDay: number | null; daysPerWeek: number | null };
  derived: {
    stage: string | null; background: string | null;
    monthsToGraduation: number | null; computedAt: string;
  };
  status: {
    onboardingCompleted: boolean; contextVersion: number;
    missing: string[]; completedAt: string | null;
  };
}


/** One required skill compared with what the student has demonstrated. */
export interface ReadinessSkill {
  skillKey: string;
  skillName: string;
  importance: string;
  targetLevel: string;
  targetScore: number;
  /** Null when never measured — never 0, which would assert a failure we did not observe. */
  studentScore: number | null;
  skillConfidence: string | null;
  evidenceCount: number;
  gapPoints: number | null;
  status: string;
}

/** Readiness is unavailable for distinct, differently-fixable reasons. */
export interface RoleReadinessUnavailable {
  available: false;
  /** YEAR_1: role readiness opens in Year 2 (Year 1 has no role). */
  reason: 'ROLE_NOT_SELECTED' | 'ROLE_BLUEPRINT_NOT_READY' | 'NO_EVIDENCE' | 'YEAR_1';
  message: string;
  role?: { key: string; name?: string };
}

export interface RoleReadinessAvailable {
  available: true;
  role: { key: string; name: string };
  /** Null when nothing is sufficiently measured — not 0, which would assert unreadiness. */
  readiness: number | null;
  /** How much of the role has trustworthy evidence. Always shown beside readiness. */
  coverage: number;
  /** Under 40% of the role measured: `readiness` is an early estimate, not the headline. */
  provisional?: boolean;
  /** Readiness against the whole role, unmeasured skills counted as 0. */
  wholeRoleReadiness?: number;
  confidence: string;
  summary: {
    requiredSkills: number; assessedSkills: number;
    priorityGaps: number; needsWork: number; onTrack: number; strong: number;
    limitedEvidence: number; notAssessed: number;
    essentialTotal: number; essentialAssessed: number;
  };
  skills: ReadinessSkill[];
  topGaps: ReadinessSkill[];
  strengths: ReadinessSkill[];
  assessmentNeeded: ReadinessSkill[];
}

export type RoleReadinessResponse = RoleReadinessAvailable | RoleReadinessUnavailable;

/** One skill as the student's own evidence describes it. Score and confidence are separate. */
export interface SkillDnaRow {
  skillKey: string;
  skillName: string;
  /** 0-100, weighted performance across observations. */
  score: number;
  /** How much evidence sits behind the score — NOT how good the student is. */
  confidence: string;
  evidenceCount: number;
  /** Distinct questions; repeats of one item are weaker evidence than several. */
  distinctItems: number;
  lastEvidenceAt: string | null;
  skillActive: boolean;
}

/** A generated paper's shape and chosen items. Diagnostic — nothing is persisted. */
export interface AssessmentPreview {
  ok: boolean;
  message?: string;
  context: {
    name?: string; stage: string; roleKey: string; discovery: boolean;
    policy: string; policyKey?: string; policyVersion?: number; blueprintVersion?: number;
  };
  specification?: {
    skillCoverage: Record<string, number>;
    difficultyCoverage: Record<string, number>;
    totalPoints: number;
  };
  report?: {
    requestedSlots: number; filled: number; exactMatches: number;
    difficultyFallbacks: number; repeatedFromPreviousAttempt: number;
  };
  items?: {
    order: number; skillKey: string; difficulty: string;
    servedDifficulty: string | null; reason: string; sourceType: string; text: string;
  }[];
  /** Present when generation failed for want of mapped evidence. */
  shortfalls?: { skillKey: string; difficulty: string; wanted: number; got: number }[];
}

export interface AssessmentPolicyRow {
  key: string; stage: string; label: string; version: number;
  skillSlots: number; maxSkills: number;
  difficultyMix: { EASY: number; MEDIUM: number; HARD: number };
  prerequisiteDepth: number;
  allowedSkillDifficulty: string[];
}

/** One skill an assessment item measures. Joined with the skill for display. */
export interface ItemEvidenceRow {
  skillKey: string;
  contribution: string;
  active: boolean;
  skillName: string;
  skillActive: boolean;
  skillAssessable: boolean;
  /** The key resolves to nothing in the skill graph — surfaced so it can be repaired. */
  missing: boolean;
}

/** A piece of assessment content, normalised across the four content families. */
export interface EvidenceItem {
  sourceType: string;
  sourceId: string;
  sourceParentId?: string;
  text: string;
  itemType: string;
  /** Normalised on read from each family's own scale; null where none exists. */
  difficulty: string | null;
  /** The source's own tag. NOT a skill. */
  sourceTag: string | null;
  evidence: ItemEvidenceRow[];
  primarySkillKey: string | null;
  stale: boolean;
}

export interface MappableSkill {
  key: string; name: string; aliases: string[];
  parentKey: string | null; difficulty: string;
}

export interface SkillCoverageRow {
  skillKey: string; skillName: string;
  active: boolean; assessable: boolean;
  total: number; primary: number;
  byType: Record<string, number>;
}

/** One skill a role expects, with the skill's own details joined in for display. */
export interface BlueprintRequirement {
  skillKey: string;
  importance: string;
  weight: number;
  targetLevel: string;
  /** Which years this applies to. Empty means all of them, which is the default. */
  years?: string[];
  /** The same skill at a different bar per year. Falls back to targetLevel. */
  yearTargets?: { year: string; targetLevel: string }[];
  active: boolean;
  displayOrder: number;
  note?: string;
  /** Joined from CareerSkill. Never stored on the requirement — the key is the truth. */
  skillName: string;
  skillDescription: string;
  skillNodeType: string;
  skillDifficulty: string;
  parentKey: string | null;
  /** False once Module 3 retires the skill; the requirement itself survives. */
  skillActive: boolean;
  /** The key resolves to nothing at all — surfaced rather than hidden. */
  missing: boolean;
}

export interface ResolvedBlueprint {
  roleKey: string;
  roleName: string;
  roleActive: boolean;
  domainKey: string;
  published: boolean;
  version: number;
  requirements: BlueprintRequirement[];
  summary: {
    total: number;
    active: number;
    byImportance: Record<string, number>;
    totalWeight: number;
    /** Requirements pointing at a deactivated or missing skill. */
    stale: number;
  };
  updatedAt?: string;
  updatedBy?: string;
}

export interface BlueprintRoleRow {
  key: string;
  name: string;
  active: boolean;
  studentSelectable: boolean;
  blueprint: { total: number; active: number; published: boolean };
}

/**
 * A canonical skill. `parentKey` is where it sits in the taxonomy; `prerequisiteKeys` is
 * what must be learned first — a different relationship, often pointing across branches.
 */
export interface AdminSkill {
  id: string;
  key: string;
  domainKey: string;
  name: string;
  shortName: string;
  description: string;
  /** GROUP organises; SKILL is the thing that gets measured. */
  nodeType: string;
  parentKey: string | null;
  prerequisiteKeys: string[];
  difficulty: string;
  aliases: string[];
  displayOrder: number;
  active: boolean;
  assessable: boolean;
  learnable: boolean;
  /** Part of the shipped taxonomy — editable and deactivatable, never deletable. */
  systemSkill: boolean;
  updatedBy?: string;
  updatedAt?: string;
}

/** The same skill, nested for the tree view. */
export interface SkillNode {
  id: string;
  key: string;
  name: string;
  nodeType: string;
  difficulty: string;
  description: string;
  parentKey: string | null;
  prerequisiteKeys: string[];
  aliases: string[];
  displayOrder: number;
  active: boolean;
  assessable: boolean;
  learnable: boolean;
  systemSkill: boolean;
  children: SkillNode[];
}

/**
 * A career role as ADMIN sees it. The student-facing shape is deliberately smaller —
 * see CareerContextOptions.roles, which carries no configuration metadata.
 */
export interface AdminCareerRole {
  id: string;
  key: string;
  domainKey: string;
  name: string;
  shortName: string;
  description: string;
  studentDescription: string;
  iconKey: string;
  aliases: string[];
  displayOrder: number;
  active: boolean;
  studentSelectable: boolean;
  /** Seeded by the product; cannot be deleted, only hidden. */
  systemRole: boolean;
  updatedBy?: string;
  updatedAt?: string;
}

/**
 * Whether a personalized assessment can be started, decided entirely server-side.
 *
 * `reasonCode` is a stable token so the UI can pick a state without parsing the message;
 * `message` is the sentence to show. Neither carries internal detail.
 */
export interface AssessmentAvailability {
  assessmentAvailable: boolean;
  reasonCode?:
    | 'ACCOUNT_NOT_FOUND'
    | 'CONTEXT_INCOMPLETE'
    | 'STAGE_UNKNOWN'
    | 'ROLE_NOT_CONFIGURED'
    | 'BLUEPRINT_UNPUBLISHED'
    | 'BLUEPRINT_EMPTY'
    | 'SKILLS_NOT_CONFIGURED'
    | 'QUESTION_POOL_EMPTY';
  message?: string;
  /** The member has no chosen role and would sit the broad discovery paper. */
  discovery: boolean;
  /** An attempt is already open — the CTA resumes rather than starts. */
  inProgress: boolean;
  /** They have already submitted one — offering "Start" as the default creates a second. */
  alreadyCompleted?: boolean;
}

/** One stage's paper shape, as the admin screen edits it. */
export interface EditablePolicy {
  stage: string;
  label: string;
  defaults: {
    skillSlots: number; maxSkills: number;
    /** Absent when the API is older than this bundle. */
    minItemsPerSkill?: number;
    difficultyMix: { EASY: number; MEDIUM: number; HARD: number };
  };
  skillSlots: number;
  maxSkills: number;
  difficultyMix: { EASY: number; MEDIUM: number; HARD: number };
  /** 0 = untimed, which is the shipped behaviour. */
  timeLimitMinutes: number;
  overridden: boolean;
  /** Read-only context — what this stage is allowed to ask about. */
  allowedSkillDifficulty: string[];
  /** Editable: how many questions each covered skill is asked. */
  minItemsPerSkill: number;
  maxItemsPerSkill: number;
  /**
   * What these settings really produce, computed server-side.
   *
   * The three numbers interact in a way no one should have to work out from the form: slots
   * divided by questions-per-skill caps the skills, and a skill asked too few times counts for
   * nothing however long the paper is.
   */
  effective?: {
    skills: number;
    itemsPerSkill: number;
    slotsUsed: number;
    itemsForConfidence: number;
    measuresReliably: boolean;
  };
}

export interface PolicyBounds {
  skillSlots: { min: number; max: number };
  maxSkills: { min: number; max: number };
  /** Absent when the API is older than this bundle. */
  itemsPerSkill?: { min: number; max: number };
  timeLimitMinutes: { min: number; max: number };
}

/** Served alongside the context so the UI never hardcodes a list the server would reject. */
/** Filters the question bank accepts. Every one of them is a field that actually exists. */
export interface QuestionBankQuery {
  skillKey?: string; difficulty?: string; provenance?: string; status?: string;
  targeting?: string; year?: string; course?: string; branch?: string; role?: string;
  search?: string; page?: number; pageSize?: number;
}

export interface QuestionAudience {
  audienceRoles: string[]; audienceYears: string[];
  audienceCourses: string[]; audienceBranches: string[];
}

export interface QuestionBankRow {
  sourceType: string;
  sourceId: string;
  question: string;
  options: { text: string; isCorrect: boolean }[];
  explanation: string;
  difficulty: string | null;
  /** False means it is shared with the LMS quiz bank and must be copied before editing. */
  owned: boolean;
  /** 'careerpilot' ours · 'lms' shared with quizzes · 'exam' the skill-assessment bank. */
  origin: 'careerpilot' | 'lms' | 'exam';
  active: boolean;
  skills: { skillKey: string; skillName: string; contribution: string }[];
  audience: QuestionAudience;
  /** Non-zero freezes option structure: answers name options by position. */
  answerCount: number;
  editable: { text: boolean; optionText: boolean; optionStructure: boolean; hardDelete: boolean };
}

export interface QuestionBankPage {
  rows: QuestionBankRow[]; total: number; page: number; pageSize: number;
}

export interface QuestionBankEdit {
  question?: string;
  options?: { text: string; isCorrect: boolean }[];
  explanation?: string;
  difficulty?: string;
  audience?: QuestionAudience;
}

export interface CareerContextOptions {
  domains: { key: string; label: string }[];
  /** From admin configuration. `iconKey` is optional and purely presentational. */
  roles: { key: string; label: string; blurb: string; iconKey?: string }[];
  languages: string[];
  availability: { minutes: number; label: string }[];
  /** Paired with `availability` — the roadmap needs both to size weekly capacity. */
  daysPerWeek: { days: number; label: string }[];
  /** All three come from the tenant's onboarding configuration, not from constants. */
  programs: string[];
  academicYears: string[];
  /**
   * Empty when the tenant has not curated a branch list, in which case the screen keeps a
   * free-text box rather than showing a picker with nothing in it.
   */
  branches?: string[];
  stages: { key: string; label: string; blurb: string }[];
}

export interface CareerContextPatch {
  domain?: string;
  primaryRole?: string;
  secondaryRole?: string | null;
  preferredProgrammingLanguages?: string[];
  minutesPerDay?: number;
  daysPerWeek?: number;
  program?: string; degree?: string; branch?: string;
  currentAcademicYear?: string; graduationYear?: number | null;
  complete?: boolean;
}

export interface RuleVocabulary {
  goals: string[];
  stages: { key: string; label: string; who: string }[];
  backgrounds: { key: string; label: string }[];
  categories: { key: string; label: string }[];
}
export interface RulePreviewRow {
  key: string; label: string; enabled: boolean; fallback: boolean; priority: number;
  members: number;
  samples: { name: string; email: string; why: string }[];
}
export interface RulePreview {
  total: number;
  errors: string[];
  warnings: string[];
  /** Whether these numbers describe what happens, or simulate what would. */
  active: boolean;
  /** Members reaching the catch-all, i.e. accounted for by no rule. */
  viaFallback: number;
  unmatched: number;
  /** How many would land somewhere other than where they are today. */
  moved: number;
  rows: RulePreviewRow[];
}
export interface ReevaluateResult {
  applied: boolean;
  total: number;
  changeCount: number;
  changes?: { id: string; name: string; email: string; from: string | null; to: string; toLabel: string }[];
}

// ── 90-day roadmap (Module 9) ──────────────────────────────────────────────────
//
// A plan, not a reading. Unlike readiness it is stored, so what a member was asked to do
// last week survives this week's evidence changing.

export interface RoadmapObjective {
  skillKey: string;
  skillName: string;
  workType: 'LEARN' | 'PRACTICE' | 'ASSESS' | 'REVIEW';
  plannedMinutes: number;
  phase: string;
  week: number;
  sequence: number;
  reasonCode: 'PRIORITY_GAP' | 'NEEDS_WORK' | 'PREREQUISITE' | 'ASSESSMENT_NEEDED'
    | 'LIMITED_EVIDENCE' | 'MAINTENANCE' | 'VALIDATION';
  targetLevel: string;
  /** Built deterministically from the numbers — never generated, always safe to show. */
  explanation: string;
  origin: 'GENERATED' | 'MANUAL';
}

export interface RoadmapPhase {
  key: string; title: string; blurb: string;
  fromWeek: number; toWeek: number; fromDay: number; toDay: number;
  plannedMinutes: number;
}

export interface SkillRoadmap {
  id: string;
  role: { key: string; name: string };
  policyVersion: string;
  roadmapVersion: number;
  startDate: string;
  endDate: string;
  roadmapDays: number;
  weekCount: number;
  generatedAt: string;
  planningConfidence: 'LOW' | 'MEDIUM' | 'HIGH';
  capacity: {
    minutesPerDay: number; daysPerWeek: number;
    weeklyCapacityMinutes: number; plannedMinutes: number;
  };
  basis: {
    readiness: number | null; coverage: number;
    careerStage: string | null; entitlementLimited: boolean;
  };
  phases: RoadmapPhase[];
  objectives: RoadmapObjective[];
  deferred: { skillKey: string; skillName: string; reasonCode: string; reason: string }[];
  summary: { objectives: number; deferred: number; skills: number };
}

export interface SkillRoadmapAvailable {
  available: true;
  currentDay: number;
  currentWeek: number;
  completed: boolean;
  outdated: boolean;
  outdatedReasons: ('ROLE_CHANGED' | 'COMMITMENT_CHANGED' | 'BLUEPRINT_CHANGED')[];
  roadmap: SkillRoadmap;
  created?: boolean;
}

export interface SkillRoadmapUnavailable {
  available: false;
  reason: 'CAREER_CONTEXT_INCOMPLETE' | 'ROLE_NOT_SELECTED' | 'ROLE_BLUEPRINT_NOT_READY'
    | 'NO_READINESS_DATA' | 'MEMBERSHIP_REQUIRED';
  message: string;
  missing?: string[];
  role?: { key: string; name?: string };
}

export type SkillRoadmapResponse = SkillRoadmapAvailable | SkillRoadmapUnavailable;

// ── Personalised assessment + daily execution (Module 10) ─────────────────────

/** One question as the student sees it. There is no correct answer in this shape. */
export interface SkillAssessmentItem {
  order: number;
  sourceType: string;
  sourceId: string;
  text: string;
  itemType: string;
  /** The code the question is about — debug / predict-output / complete-code items. */
  codeSnippet?: string;
  language?: string;
  options?: { id: string; text: string }[];
  points: number;
  /** What was saved earlier, so a resumed paper comes back filled in. */
  response?: any;
  /** Saved as "I don't know". */
  dontKnow?: boolean;
}

/* ── Orientation: the welcome days before Day 1 ── */
export type OrientationItemKind = 'video' | 'notes' | 'image' | 'checklist' | 'recording';

export interface OrientationItem {
  key: string;
  kind: OrientationItemKind;
  title: string;
  blurb?: string;
  /** Empty on a video means the admin has not added it yet, and the screen says so. */
  url?: string;
  body?: string;
  items?: string[];
  targetSeconds?: number;
  required: boolean;
  estimatedMinutes: number;
  done?: boolean;
  checked?: number[];
  recordingDurationSec?: number | null;
}

export interface OrientationDay {
  dayNumber: number;
  /** What a member sees: "0.3". `dayNumber` is the row; this is the name it goes by. */
  day: string;
  title: string;
  blurb: string;
  items: OrientationItem[];
  done?: boolean;
  locked?: boolean;
  /** Why it is shut: the day before it is unfinished, or the calendar has not reached it. */
  lockedReason?: 'PREVIOUS_DAY' | 'NOT_TODAY_YET';
  /** The IST midnight that opens it, when the calendar is what is holding it. */
  opensAt?: string | null;
  minutes?: number;
}

/**
 * A welcome day as it is AUTHORED, which is not a welcome day as it is SERVED.
 *
 * The admin endpoints below traffic in the programme definition. `day` — the "0.3" a member
 * sees — is derived by the server from the day's position when it builds a member's view, so it
 * is not a field an admin owns or can send. Omitting the one field keeps the two shapes from
 * drifting apart.
 */
export type AuthoredOrientationDay = Omit<OrientationDay, 'day'>;

export interface OrientationView {
  enabled: boolean;
  /** True while this member must finish orientation before their first learning day. */
  mandatory: boolean;
  /**
   * One welcome day per calendar day, in India time, as well as one after another.
   *
   * Members who were already learning when pacing arrived are not paced, so this is false for
   * them and the screens behave exactly as they did.
   */
  paced?: boolean;
  complete: boolean;
  days: OrientationDay[];
  nextDay: number | null;
  totalDays: number;
  completedDays: number;
}

export interface PlacementAvailability {
  available: boolean;
  topicTitle?: string | null;
  /** Days of this topic still ahead — what passing could save. */
  daysAhead?: number;
  questions?: number;
  refused?: 'TOPIC_NOT_IN_PLAN' | 'TOPIC_STARTED' | 'NOTHING_MEASURABLE' | 'ASSESSMENT_IN_PROGRESS' | 'COOLDOWN_ACTIVE';
  message?: string;
  availableAt?: string | null;
}

/** What a submitted placement check returns, from either submit route. */
export interface PlacementResult {
  ok: boolean;
  placementCheck?: boolean;
  message?: string;
  score?: number;
  skillScores?: { skillKey: string; earned: number; max: number; percentage: number }[];
  daysBefore?: number;
  daysAfter?: number;
}

export interface SkillAssessment {
  /** The question the member was on when they last saved. */
  lastPosition?: number;
  id: string;
  attemptNumber: number;
  /** 0 = untimed. */
  timeLimitMinutes?: number;
  /** Server-computed, so a reload resumes with the time actually left. */
  secondsRemaining?: number | null;
  status: 'IN_PROGRESS' | 'SUBMITTED' | 'ABANDONED';
  /** INITIAL, REASSESSMENT, SKILL_CHECK, MODULE_ASSESSMENT or PLACEMENT_CHECK. */
  purpose?: string;
  startedAt: string;
  totalQuestions: number;
  items: SkillAssessmentItem[];
}

/**
 * One step of a student's journey through a skill.
 *
 * `topic` and `subtopic` are grouping, not skills — "Loops" divides into "For loops", which
 * divides again into "Counting with range". Order is `sequence`; the labels never reorder
 * anything, so a journey may return to a topic later and the screen shows it where it falls.
 */
export interface ConceptJourneyStep {
  stepId: string;
  sequence: number;
  phase: string;
  workType: 'LEARN' | 'PRACTICE' | 'ASSESS' | 'REVIEW';
  topic: string;
  subtopic: string;
  title: string;
  resourceId: string;
  resourceType: string;
  /** Where it opens. Empty when nothing is attached; absolute for an external link. */
  route: string;
  /** False when the step points at a retired or empty material — it would open nothing. */
  hasContent: boolean;
  estimatedMinutes: number;
  required: boolean;
  done: boolean;
}

export interface ConceptJourney {
  skillKey: string;
  title: string;
  description: string;
  learningOutcomes: string[];
  version: number;
  status: string;
  estimatedMinutes: number;
  /** The first step not yet done — what "Continue" opens. Null once the journey is finished. */
  nextStepId: string | null;
  progress: { completed: number; totalRequired: number; percent: number };
  steps: ConceptJourneyStep[];
}

export interface DailyMission {
  key: string;
  roadmapId: string;
  objectiveSequence: number;
  skillKey: string;
  skillName: string;
  workType: 'LEARN' | 'PRACTICE' | 'ASSESS' | 'REVIEW';
  plannedMinutes: number;
  title: string;
  explanation: string;
  reasonCode: string;
  resourceState: 'READY' | 'RESOURCE_NOT_CONFIGURED';
  resource?: { type: string; id: string; title: string; route: string };
  done: boolean;
}

export interface DailyPlanAvailable {
  available: true;
  policyVersion: string;
  roadmapId: string;
  date: string;
  roadmapDay: number;
  roadmapWeek: number;
  weekCount: number;
  capacity: { minutesPerDay: number; plannedMinutes: number };
  missions: DailyMission[];
  /** Progress through the PLAN. Deliberately not a readiness figure. */
  progress: { plannedMinutes: number; completedMinutes: number; percent: number };
  week: { plannedMinutes: number; completedMinutes: number };
  unmappedObjectives: number;
}

export interface DailyPlanUnavailable {
  available: false;
  reason: 'ROADMAP_REQUIRED' | 'ROADMAP_COMPLETED' | 'MEMBERSHIP_REQUIRED';
  message: string;
}

export type DailyPlanResponse = DailyPlanAvailable | DailyPlanUnavailable;

// ── Gamification (Module 11) ─────────────────────────────────────────────────
//
// XP is the ENGAGEMENT score: non-redeemable, and never a statement about ability. Coins
// remain the reward currency on their own engine; nothing here converts between them.

export interface GamificationBadgeView {
  key: string;
  name: string;
  description: string;
  iconKey: string;
  earned: boolean;
  awardedAt: string | null;
}

export interface RankView {
  available: boolean;
  /** Null when unavailable — never 0, which would read as "last". */
  rank: number | null;
  participants?: number;
  reason?: string;
}

export interface GamificationSummary {
  xp: number;
  /** The server sends the whole LevelInfo (passportGamificationService.levelFromXp), including progressPct. */
  level: LevelInfo;
  streak: number;
  longestStreak: number;
  badges: GamificationBadgeView[];
  earnedCount: number;
  ranks: Record<string, RankView>;
  /** XP → coins, when an admin switched it on. */
  conversion?: { xpPerCoin: number; availableXp: number; coinsAvailable: number; xpToNextCoin: number } | null;
}

export interface XpHistoryEntry {
  eventKey: string;
  amount: number;
  at: string;
  sourceType: string;
}

export interface ScopedLeaderboardRow {
  rank: number;
  studentId: string;
  name: string;
  college: string | null;
  xp: number;
  me: boolean;
}

/** Distinct from the legacy tenant-only LeaderboardResponse above, which is untouched. */
export type ScopedLeaderboardResponse =
  | {
      available: true;
      scope: string; period: string;
      entries: ScopedLeaderboardRow[];
      myRank: number | null;
      myXp: number;
      participantCount: number;
    }
  | { available: false; scope: string; period: string; reason: string; myRank: null };

// ── Rewards (Module 12) ──────────────────────────────────────────────────────
//
// COINS buy rewards; XP never does. The business cost of a reward is deliberately absent
// from every shape here — it is not the student's business.

export interface RewardCard {
  key: string;
  name: string;
  description: string;
  type: string;
  iconKey: string;
  imageUrl?: string;
  coinCost: number;
  stockMode: string;
  stockAvailable: number | null;
  availableUntil?: string;
  instructions?: string;
  eligibility: {
    eligible: boolean;
    reasons: string[];
    messages: string[];
    coinsShort: number;
    remainingStudentLimit: number | null;
  };
}

export interface RewardCatalogue {
  rewards: RewardCard[];
  student: {
    coins: number;
    coinBalance: number;
    expiredCoins: number;
    minRedemption: number;
    xp: number;
    level: number;
    canSpend: boolean;
  };
  /** XP → coins, when switched on: lets the store say "convert your XP". */
  conversion?: { xpPerCoin: number; coinsAvailable: number; xpToNextCoin: number } | null;
}

/** A reward as the admin edits it. Costs: coins to the member, paise to the business. */
export interface AdminReward {
  _id?: string;
  key: string; name: string; description: string;
  type: 'PHYSICAL' | 'DISCOUNT' | 'ACCESS' | 'COUPON' | 'RECOGNITION';
  iconKey: string; imageUrl?: string; instructions?: string;
  coinCost: number; budgetCostPaise: number;
  active: boolean; studentVisible: boolean;
  stockMode: 'UNLIMITED' | 'LIMITED'; stockAvailable: number; stockReserved?: number; stockFulfilled?: number;
  perStudentLimit: number; totalRedemptionLimit: number; totalRedeemed?: number;
  minimumXp: number; minimumLevel: number;
  availableFrom?: string | null; availableUntil?: string | null;
  displayOrder: number;
}

export interface AdminRedemption {
  _id: string; studentId: string; rewardKey: string; rewardName: string; rewardType: string;
  coinCost: number; budgetCostPaise: number;
  status: 'PENDING' | 'RESERVED' | 'FULFILLED' | 'CANCELLED';
  requestedAt: string; reservedAt?: string; fulfilledAt?: string; cancelledAt?: string;
  fulfillmentReference?: string; adminNotes?: string; cancelReason?: string;
  studentSnapshot?: { displayName?: string; collegeName?: string };
}

export interface RewardBudgetView {
  summary: {
    enabled: boolean; mode: 'MANUAL' | 'PERCENTAGE'; period: string;
    basisPoints: number; effectiveFrom: string | null; studentsRewarded: number;
    display: { revenueBase: number; calculatedBudget: number; cap: number; effectiveBudget: number; reserved: number; redeemed: number; available: number; averageReward: number };
  };
  policy: { enabled: boolean; mode: 'MANUAL' | 'PERCENTAGE'; manualBudgetPaise: number; basisPoints: number; capPaise: number; effectiveFrom?: string | null };
}

export interface RedemptionRow {
  id: string;
  rewardKey: string;
  rewardName: string;
  rewardType: string;
  coinCost: number;
  status: 'PENDING' | 'RESERVED' | 'FULFILLED' | 'CANCELLED';
  requestedAt: string;
  fulfilledAt?: string;
  cancelledAt?: string;
  fulfillmentReference?: string;
  refunded: number;
}

// ── Skill check-in and adaptive replanning (Module 13) ───────────────────────
//
// A check-in re-measures a few skills and reports what changed. It never moves the roadmap:
// that happens only when the student calls replanMySkillRoadmap().

export interface ReassessmentStatus {
  eligible: boolean;
  blockers: string[];
  triggers: string[];
  lastCompletedAt: string | null;
  nextEligibleAt: string | null;
  cooldownDays: number;
  targetSkills: { skillKey: string; skillName: string }[];
  estimatedQuestions: number;
  activeAttemptId: string | null;
  message: string;
}

export interface SkillDeltaView {
  skillKey: string;
  skillName: string;
  before: number | null;
  after: number | null;
  /** Null when one side was never measured — an unknown becoming known is not a delta. */
  delta: number | null;
  beforeStatus: string | null;
  afterStatus: string | null;
  materialReasons: string[];
}

export interface ReassessmentResult {
  ok: boolean;
  message?: string;
  skills?: SkillDeltaView[];
  readinessBefore?: number | null;
  readinessAfter?: number | null;
  readinessDelta?: number | null;
  targetSkillKeys?: string[];
  completedAt?: string;
}

export interface ReplanStatusView {
  recommendation: 'NONE' | 'SUGGESTED' | 'REQUIRED';
  structuralReasons: string[];
  affectedSkills: SkillDeltaView[];
  currentReadiness: number | null;
  roadmapBaselineReadiness: number | null;
  readinessDelta: number | null;
  hasActiveRoadmap: boolean;
  roadmapCompleted: boolean;
  message: string;
}

// ── Placement readiness (Module 14) ──

export type ClaimStatus =
  | 'VERIFIED' | 'NEEDS_VALIDATION' | 'CLAIM_EXCEEDS_EVIDENCE' | 'MISSING_FROM_RESUME';

export interface ResumeClaimView {
  skillKey: string;
  skillName: string;
  status: ClaimStatus;
  message: string;
  /** Null means never measured — which is not the same as measured at zero. */
  measuredScore: number | null;
  requiredByRole: boolean;
}

export interface ResumeReadinessView {
  available: boolean;
  reason?: 'NO_RESUME' | 'ROLE_NOT_SELECTED' | 'ROLE_BLUEPRINT_NOT_READY';
  message?: string;
  policyVersion?: string;
  role?: { key: string; name: string };
  readiness?: number;
  dimensions?: { dimension: string; score: number; detail: string }[];
  claims?: ResumeClaimView[];
  recommendations?: { priority: 'CRITICAL' | 'IMPORTANT' | 'OPTIONAL'; action: string; skillKey?: string }[];
}

export interface InterviewReadinessView {
  available: boolean;
  reason?: string;
  message?: string;
  interviewId?: string;
  role?: string;
  completedAt?: string;
  readiness?: number;
  /** Only the dimensions the evaluator actually measured. A missing one is missing, not zero. */
  dimensions?: { dimension: string; score: number }[];
  perSkill?: { skillKey: string; area: string; score: number }[];
}

export interface PlacementReadinessView {
  policyVersion: string;
  skill: {
    available: boolean;
    reason?: string;
    role?: { key: string; name: string };
    readiness?: number | null;
    coverage?: number;
    confidence?: 'LOW' | 'MEDIUM' | 'HIGH';
  };
  resume: ResumeReadinessView;
  interview: InterviewReadinessView;
}

export interface InterviewCoverageView {
  ok: boolean;
  message?: string;
  role?: { key: string; name: string };
  targets?: { skillKey: string; skillName: string; slots: number; bands: ('core' | 'gaps' | 'strengths')[] }[];
}

// ── Concept bank types ──
export interface ConceptRow {
  key: string;
  name: string;
  domainKey: string;
  difficulty?: string;
  learnable: boolean;
  assessable: boolean;
  materialCount: number;
  byWorkType: Record<string, number>;
  /** The plan can ask a member to LEARN this and have nothing to offer them. */
  missingLearn: boolean;
}

export interface ConceptSummary {
  total: number;
  withAnyMaterial: number;
  missingLearn: number;
}

export interface AudienceOptions {
  years: string[];
  courses: string[];
  branches: string[];
  roles: string[];
  languages: string[];
  stages: string[];
}

export interface MaterialAudience {
  years: string[]; courses: string[]; branches: string[];
  roles: string[]; languages: string[]; stages: string[];
}

export interface MaterialStep {
  title: string; detail: string; command?: string; expectedOutput?: string;
}
export interface MaterialBreakdown { term: string; explanation: string; example?: string }
export interface MaterialCheck { question: string; answer: string }

export interface MaterialAttachment {
  fileKey: string;
  fileName: string;
  mimeType: string;
  size: number;
  storage: 'bunny' | 'local';
  uploadedAt?: string;
}

export interface MaterialBody {
  overview?: string;
  notes?: string;
  videoUrl?: string;
  videoKey?: string;
  steps: MaterialStep[];
  breakdown: MaterialBreakdown[];
  checks: MaterialCheck[];
  references: { label: string; url: string }[];
  attachments: MaterialAttachment[];
}

export interface MaterialRow {
  id: string;
  skillKey: string;
  skillName: string;
  resourceType: string;
  resourceId: string;
  title: string;
  description: string;
  url: string;
  fileKey: string;
  language: string;
  /** Null means "use the tenant's flat rate". */
  xp: number | null;
  audience: MaterialAudience;
  scoreWindow: { min: number | null; max: number | null };
  body: MaterialBody;
  resourceTitle: string | null;
  resourceMissing: boolean;
  workTypes: string[];
  priority: number;
  active: boolean;
}

export const emptyAudience = (): MaterialAudience => ({
  years: [], courses: [], branches: [], roles: [], languages: [], stages: [],
});

export const emptyBody = (): MaterialBody => ({
  overview: '', notes: '', videoUrl: '', videoKey: '',
  steps: [], breakdown: [], checks: [], references: [], attachments: [],
});


/* ── Stage curriculum: the one screen that joins what a stage teaches to what it can measure ── */

export interface StageSkillNode {
  skillKey: string;
  skillName: string;
  /** Taxonomy band, not question difficulty. */
  difficulty: string | null;
  active: boolean;
  assessable: boolean;
  /** Distinct questions available in each band the generator draws on. */
  questions: { easy: number; medium: number; hard: number; total: number };
  inStageSet: boolean;
  measurable: boolean;
}

export interface StageTopicNode {
  id: string;
  topicCode: string | null;
  title: string;
  description?: string;
  order: number;
  startDay: number;
  endDay: number;
  defaultDepth: string | null;
  mandatory: boolean;
  /** Part of the mandatory Foundation backbone: every student covers it, at the depth their evidence earns. */
  backbone: boolean;
  applicableDirections: string[];
  learningOutcomes: string[];
  prerequisiteSkillKeys: string[];
  skills: StageSkillNode[];
  unknownSkillKeys: string[];
}

export interface StageModuleNode {
  moduleCode: string;
  moduleName: string;
  displayOrder: number;
  blurb?: string;
  topics: StageTopicNode[];
  skillCount: number;
  measurableCount: number;
}

export interface StageCurriculumView {
  stage: string;
  stageLabel: string;
  curriculum: { id: string; title: string; totalDays: number; isPublished: boolean } | null;
  policy: { skillSlots: number; maxSkills: number; itemsPerSkill: number };
  modules: StageModuleNode[];
  totals: {
    modules: number; topics: number; skills: number;
    measurable: number; inStageSet: number; unmapped: number;
  };
  measuredButNotTaught: string[];
}

export interface StageTopicInput {
  title?: string;
  description?: string;
  moduleCode?: string;
  topicCode?: string;
  skillKeys?: string[];
  prerequisiteSkillKeys?: string[];
  defaultDepth?: string;
  mandatory?: boolean;
  backbone?: boolean;
  applicableDirections?: string[];
  learningOutcomes?: string[];
  order?: number;
}

// ── Roadmap V2 (admin): settings, per-year budgets, topic priorities ───────────────────────────
export type TopicPriority = 'MUST' | 'SHOULD' | 'OPTIONAL';
export interface RoadmapV2Settings {
  enabled: boolean; stages: string[]; studentIds: string[]; dailyMinutes: number; revisionDays: number;
  /** V2 only: every roadmap is V2, and V1 never builds or updates one again. */
  v2Only: boolean;
}
export interface RoadmapV2Budget {
  programDays: number; dailyMinutes: number; budgetMinutes: number; bridgeMinutes: number; mustCapMinutes: number;
}
export type PrioritySummary = Record<'MUST' | 'SHOULD' | 'OPTIONAL' | 'UNSET', { topics: number; minutes: number }>;
export interface RoadmapV2Overview {
  settings: RoadmapV2Settings;
  stages: { stage: string; available: boolean; topics: number; budget: RoadmapV2Budget; summary: PrioritySummary }[];
}
export interface PriorityTopic {
  topicCode: string; title: string; minutes: number; backbone: boolean; mandatory: boolean;
  hasCheckpoint: boolean; priority: TopicPriority | null; prioritySource: 'DRAFT' | 'ADMIN' | null;
}
export interface StagePriorities {
  stage: string; available: boolean; budget: RoadmapV2Budget; summary: PrioritySummary; topics: PriorityTopic[];
}
export interface DraftStageView {
  stage: string; available: boolean; budget: RoadmapV2Budget; after: PrioritySummary;
  changes: number; adminSet: number; overCap: boolean;
}
export const roadmapV2Api = {
  previewDraft: async (): Promise<{ stages: DraftStageView[] }> =>
    (await axios.get(`${BASE}/admin/topic-priorities-draft`, { headers: auth() })).data,
  applyDraft: async (): Promise<{ written: number; stages: DraftStageView[] }> =>
    (await axios.post(`${BASE}/admin/topic-priorities-draft`, {}, { headers: auth() })).data,
  overview: async (): Promise<RoadmapV2Overview> =>
    (await axios.get(`${BASE}/admin/roadmap-v2`, { headers: auth() })).data,
  saveSettings: async (patch: Partial<RoadmapV2Settings>) =>
    (await axios.put(`${BASE}/config`, { roadmapV2: patch }, { headers: auth() })).data,
  topics: async (stage: string): Promise<StagePriorities> =>
    (await axios.get(`${BASE}/admin/topic-priorities/${stage}`, { headers: auth() })).data,
  setPriority: async (stage: string, topicCode: string, priority: TopicPriority): Promise<StagePriorities> =>
    (await axios.put(`${BASE}/admin/topic-priorities/${stage}/${encodeURIComponent(topicCode)}`, { priority }, { headers: auth() })).data,
  /** What rebuilding this member's roadmap as V2 would do. Writes nothing. */
  previewRebuild: async (studentId: string): Promise<RebuildOutcome> =>
    (await axios.get(`${BASE}/admin/students/${studentId}/roadmap-rebuild`, { headers: auth(), validateStatus: s => s < 500 })).data,
  /** Rebuild this member's roadmap as V2: completed days kept, the rest rebuilt. */
  applyRebuild: async (studentId: string): Promise<RebuildOutcome> =>
    (await axios.post(`${BASE}/admin/students/${studentId}/roadmap-rebuild`, {}, { headers: auth(), validateStatus: s => s < 500 })).data,
  bulkStatus: async (): Promise<{ counts: RebuildCounts[]; job: RebuildJob | null }> =>
    (await axios.get(`${BASE}/admin/roadmap-rebuild`, { headers: auth() })).data,
  startBulk: async (stageKey?: string | null): Promise<{ job: RebuildJob }> =>
    (await axios.post(`${BASE}/admin/roadmap-rebuild`, stageKey ? { stageKey } : {}, { headers: auth() })).data,
};

// ── Rebuild a roadmap as V2 (admin) ─────────────────────────────────────────────────────────────
export interface RebuildDay { dayNumber: number; phase: string; titles: string[] }
export interface RebuildOutcome {
  ok: boolean;
  reason?: string;
  studentId: string;
  stageKey: string | null;
  /** FULL: nothing done yet, the whole roadmap is rebuilt. FUTURE: done days kept, the rest rebuilt. */
  mode?: 'FULL' | 'FUTURE';
  before?: { version: 'V1' | 'V2' | 'NONE'; totalDays: number; completedDays: number; days: RebuildDay[] };
  after?: { totalDays: number; keptDays: number[]; phaseDays: { revision: number; bridge: number; year: number }; days: RebuildDay[] };
  applied?: boolean;
}
export interface RebuildCounts { stageKey: string; v1: number; notStarted: number; started: number; v2: number }
export interface RebuildJob {
  running: boolean; stageKey: string | null; total: number; done: number; rebuilt: number;
  failed: { studentId: string; reason: string }[]; startedAt: string | null; finishedAt: string | null;
}

// ── Career tracks (Year 3 and 4): role + language roadmaps ──────────────────────────────────────
export type TrackPriority = 'CORE' | 'MUST' | 'SHOULD' | 'OPTIONAL';
export interface PackTopicRow { topicCode: string; title?: string | null; priority: TrackPriority }
export interface SkillPackRow { key: string; title: string; kind: 'SKILL' | 'PLACEMENT'; description: string; topics: PackTopicRow[] }
export interface CareerTrackRow {
  key: string; name: string; language: string; description: string; status: 'DRAFT' | 'LIVE'; order: number;
  specializePacks: string[]; placementPacks: string[];
  split: { memberTrackShare: number; freshTrackShare: number };
  problems: string[]; students: number;
}
export interface CareerTrackCatalogue { tracks: CareerTrackRow[]; packs: SkillPackRow[] }
export interface CoverageUnit {
  unitCode: string; title: string; unitType: string; stageKey: string; status: string;
  video: 'REAL' | 'PLACEHOLDER' | 'NONE'; notes: boolean; practice: boolean; checkpoint: boolean;
}
export interface PackCoverage { key: string; topics: { topicCode: string; title: string | null; priority: TrackPriority; units: CoverageUnit[] }[] }
export interface TopicHit { topicCode: string; title: string; stageKey: string; moduleCode: string }
export interface MyTracks { stage: string | null; current: string | null; fixed?: boolean; canChange?: boolean; tracks: { key: string; name: string; language: string; description: string | null }[] }

const keep500 = { validateStatus: (s: number) => s < 500 };
export const careerTrackApi = {
  list: async (): Promise<CareerTrackCatalogue> =>
    (await axios.get(`${BASE}/admin/career-tracks`, { headers: auth() })).data,
  createTrack: async (body: Partial<CareerTrackRow>): Promise<CareerTrackCatalogue> =>
    (await axios.post(`${BASE}/admin/career-tracks`, body, { headers: auth() })).data,
  updateTrack: async (key: string, body: Partial<CareerTrackRow>): Promise<CareerTrackCatalogue> =>
    (await axios.put(`${BASE}/admin/career-tracks/${encodeURIComponent(key)}`, body, { headers: auth() })).data,
  deleteTrack: async (key: string): Promise<CareerTrackCatalogue> =>
    (await axios.delete(`${BASE}/admin/career-tracks/${encodeURIComponent(key)}`, { headers: auth() })).data,
  createPack: async (body: Partial<SkillPackRow>): Promise<CareerTrackCatalogue> =>
    (await axios.post(`${BASE}/admin/skill-packs`, body, { headers: auth() })).data,
  updatePack: async (key: string, body: Partial<SkillPackRow>): Promise<CareerTrackCatalogue> =>
    (await axios.put(`${BASE}/admin/skill-packs/${encodeURIComponent(key)}`, body, { headers: auth() })).data,
  deletePack: async (key: string): Promise<CareerTrackCatalogue> =>
    (await axios.delete(`${BASE}/admin/skill-packs/${encodeURIComponent(key)}`, { headers: auth() })).data,
  coverage: async (key: string): Promise<PackCoverage> =>
    (await axios.get(`${BASE}/admin/skill-packs/${encodeURIComponent(key)}/coverage`, { headers: auth() })).data,
  searchTopics: async (q: string): Promise<{ topics: TopicHit[] }> =>
    (await axios.get(`${BASE}/admin/career-topics`, { headers: auth(), params: { q } })).data,
  setStudentTrack: async (studentId: string, key: string | null): Promise<{ ok?: boolean; key?: string | null; rebuilt?: boolean; message?: string }> =>
    (await axios.put(`${BASE}/admin/students/${studentId}/career-track`, { key }, { headers: auth(), ...keep500 })).data,
  myTracks: async (): Promise<MyTracks> =>
    (await axios.get(`${BASE}/me/career-tracks`, { headers: auth() })).data,
  pickMyTrack: async (key: string): Promise<{ ok?: boolean; key?: string; rebuilt?: boolean; message?: string }> =>
    (await axios.put(`${BASE}/me/career-track`, { key }, { headers: auth(), ...keep500 })).data,
};
